// src/bootstrap.js — main dashboard bootstrap module.
//
// Originally a standalone <script src="dashboard.js" defer>; moved
// into the bundle on 2026-05-12 (Phase F "rip") so it shares a
// module graph with the per-page modules in src/pages/*. The IIFE
// became an exported function (setupLegacyDashboard, kept under
// that name for git-history continuity) that main.js invokes at
// bundle load.
//
// Renamed from src/legacy/dashboard.js → src/bootstrap.js on
// 2026-05-13 (Phase J final-b). The "legacy" name was correct
// during the J6 hoist arc when ~75% of this file was migrating
// out; current contents are the auth state machine + boot
// orchestrator + the few account/whoami sub-renderers that
// genuinely belong in the bootstrap layer because they read or
// mutate closure-bound auth state (`let supabase`, `let booted`,
// `state.token`).
//
// File structure (top to bottom):
//
//   1. Module-top imports (~200 lines). Side-effect imports for
//      every module whose wireListeners() needs to run at load.
//      Named imports for what bootstrap calls directly.
//
//   2. Closure setup (COORDINATOR, route short-circuits, supabase
//      client init, ROLE_COLORS constant).
//
//   3. Polling tick + render pipeline:
//        fetchAll → refreshLeaseData → refreshLeases → setConnected
//        renderInfo / updateSidebarBadges / refreshDecisionCounts
//        renderAll (the master tick — dispatches dashboard:tick)
//
//   4. Top-level UI handlers:
//        releaseLease, toggleTheme, keyboard shortcuts,
//        settings-unlink-team click handler
//
//   5. Auth state machine:
//        signOutAfterPasswordChange (closure mutator)
//        boot() — runs once a token+session pair is established
//        renderTeamSwitcher, hideLoading
//        showAuth / showDashboard / showLinkForm / showResetForm
//        showError / showSuccess / setAuthHeading
//        startGitHubOAuth, enforceAAL2IfRequired, promptTOTPChallenge
//        checkDeletionStatus, handleAuthSuccess
//        showOnboardStep / showOnboardError / createSoloWorkspace
//        + the 4 onboarding click handlers
//
//   6. Init IIFE (line ~2090). Reads URL params + Supabase session
//      + saved team token; dispatches to handleAuthSuccess /
//      showAuth / boot. Wrapped in try/catch so any failure falls
//      back to showAuth.
//
//   7. supabase.auth.onAuthStateChange listener.
//
//   8. loadAndRenderWhoami + renderAccountIdentityPanel — touch
//      supabase + state.token, can't be hoisted.
//
//   9. AC_LEGACY bridge assignment — 9 entries, all closure-bound.
//      dashboard:ready CustomEvent fired after bridge populated so
//      page modules know they can read it.
//
// Why the IIFE wrap stays: the file declares hundreds of internal
// names that don't need to leak. Wrapping isolates them from the
// bundle's module namespace, exactly like the old standalone IIFE
// did. The bridge (window.AC_LEGACY assigned at function end) is
// the explicit handoff to page modules.
//
// dashboard-init.js (PostHog, error reporting, identifyUser) loads
// earlier in <head> and stays separate so its initialization runs
// before body parse.
import { escapeHTML } from "./util.js";
import { $ } from "./dom.js";
import {
  purposeToPath,
  friendlyResource,
  toggleEventExpanded,
  eventIsExpanded,
  hasFullEvent,
  numericEventId,
  cacheFullEvent,
} from "./format.js";
import { navigate, pageFromURL, installPopstateHandler } from "./router.js";
import { openSettingsAt } from "./pages/settings-v2.js";
import {
  installTooltip,
  updateConnectionPill,
} from "./widgets.js";
import { state } from "./state.js";
import { api, apiFetch, apiWithMessage, isCoordinatorCooling } from "./api.js";
import { showToast, showConfirm, isLocalDaemonMode } from "./ui.js";
import { setupActivityLines } from "./activity-lines.js";
import {
  markTeamUnreachable,
  markTeamReachable,
  retryTeamNow,
  isTeamUnreachable,
  isLocalDaemonDown,
} from "./team-status.js";
import { handoffsState, agentsState } from "./page-state.js";
import { scheduleDynamic, isSSEEnabled } from "./tier-policy.js";
import { refreshWorktreesBadge, loadRealConflictsSince } from "./fetchers.js";
import { aggregateChats } from "./chats.js";
import { renderLeasesPage } from "./pages/leases.js";
import { renderChatsPage } from "./pages/chats.js";
import { renderEvents } from "./pages/events.js";
import { renderAgentsPage } from "./pages/agents.js";
import { updateHero, renderOverviewEvents } from "./pages/overview.js";
import { renderHandoffsPage } from "./pages/handoffs.js";
import { renderFlaggedFeed } from "./pages/spend.js";
// team-settings.js is imported for its side effects: it wires
// document-level click + input listeners for the team-settings
// panel at module init. The fetchTeamSettings export is consumed
// directly by team-page.js — legacy itself doesn't call into it.
import "./team-settings.js";
// bu-switcher.js — sidebar Business Unit dropdown. Imported here
// because the wire-once + initial-render needs to run after the
// auth boot completes, alongside renderTeamSwitcher().
// withBuFilter is used by fetchAll() to scope /v1/teams/events
// reads to the active BU — without it, switching to a non-default
// BU wouldn't filter the events fetched by the global polling loop.
import { renderBuSwitcher, wireBuSwitcher, withBuFilter } from "./bu-switcher.js";
// analytics.js is imported for its side effects: it wires the
// document-level change + click listeners for the analytics
// window picker + upgrade button. renderAnalyticsPage is
// consumed directly by pages/analytics.js, not by legacy.
import "./analytics.js";
// Billing: startCheckout is exported for the AC_LEGACY bridge
// (analytics.js's upgrade button reads it at click time).
// renderBillingStatus is imported directly by pages/settings.js
// (Tier 2n). Module init also wires the upgrade/cancel/portal click
// handlers and installs the ?billing=success / ?tier= URL return
// handlers.
import { startCheckout } from "./billing.js";
import { loadHandoffThreads, refreshHandoffsBadge } from "./handoffs.js";
import { decryptHeartbeat, deriveAESKey, decryptHint, loadHmacKey } from "./crypto.js";
// Daemon-discovery + HMAC import flow. Module init wires the
// Machines → File Path Decryption click handlers (save / autoimport / clear).
// probeLocalDaemon + switchLocalDaemonTeam + tryAutoImportHmacKey
// are imported for bootstrap callers (renderAll, boot, setup-switch-
// btn, decrypt-banner-import).
import {
  probeLocalDaemon,
  switchLocalDaemonTeam,
  tryAutoImportHmacKey,
} from "./daemon-discovery.js";
// Decryption helpers for /v1/sync/state + events. Bootstrap calls
// these from fetchAll + refreshLeaseData. decryptHint is used here for
// the one-shot stale-key probe in fetchAll (issue #179).
import {
  buildDeviceLabelMap,
  decryptLeaseRow,
  decryptEventRow,
  mergeLeaseHistoryFromEvents,
} from "./decrypt-helpers.js";
import { gravatarURL, inlineInitialAvatar } from "./avatar.js";
// Side-effect: wires the avatar upload / clear handlers at module
// init. refreshAccountAvatarPreview is imported directly by
// pages/account.js (Tier 2n).
import "./account-avatar.js";
// Side-effect: wires the connected-accounts panel + regenerate-
// codes button. Exports are imported directly by pages/account.js
// + account-mfa.js — bootstrap doesn't re-expose them on the bridge.
import "./account-auth.js";
// Side-effect: wires the Change-email + Change-password modal
// open/cancel/submit handlers. Calls signOutAfterPasswordChange via
// the AC_LEGACY bridge after a successful password change.
import "./account-email-password.js";
// Side-effect: wires the MFA enable/disable button + the
// enrollment modal (QR + verify code + recovery codes generation).
import "./account-mfa.js";
// Side-effect: wires Sign Out / Sign Out Everywhere / Delete
// Account buttons, plus the rich showDeleteBlockersModal that
// renders sole-admin team / active-subscription resolution flows.
import "./account-actions.js";
// API tokens: create / edit / roll / delete + scope picker. Module
// init wires delegated click + change + Esc handlers.
// renderApiTokens is imported directly by pages/settings.js (Tier 2n).
import "./api-tokens.js";
// Team v2: renderTeamV2 is exported for the AC_LEGACY bridge
// (team-page.js's renderTeamPageInner calls it at the end of its
// render). Module init also wires the Team page's two delegated
// click handlers: identity-level invite/resend/revoke + legacy
// laptop-level team management.
import { renderTeamV2 } from "./team-v2.js";
// My machines panel: renderMyMachines is imported directly by
// pages/settings.js (Tier 2n). Module init wires the Add/Copy/Close/
// Advanced/Revoke/Rename delegated click handlers.
import "./my-machines.js";
// Reauth (sudo-mode) helper lives in src/reauth.js as of Phase K
// Tier 2l. The 3 account-*.js modules that need it import it
// directly (Tier 2n); bootstrap.js itself doesn't call it.
// /device + /accept one-shot landing pages. Called once each from
// boot() based on URL path. Both fully repaint the page chrome so
// they bypass the normal dashboard render path.
import {
  renderDeviceApprovalPage,
  renderAcceptInvitationPage,
} from "./landing-pages.js";
// Setup + Decrypt banners. updateSetupBanner / updateDecryptBanner
// called from renderAll + a few targeted spots in bootstrap. Module
// init wires the dismiss-button click handlers; the 3 other banner
// click branches (#setup-switch-btn, #decrypt-banner-import,
// #decrypt-banner-settings) stay in bootstrap because they cross
// into bootstrap-internal helpers.
import { updateSetupBanner, updateDecryptBanner } from "./banners.js";
import { clearAuthHintCookie, writeAuthHintCookie } from "./trial-banner.js";
import { showRecoveryCodesModal } from "./recovery-codes-modal.js";
// policy-builder.js + priority-builder.js: side-effect imports —
// their exports are consumed by policy-handlers.js (Tier 2p), not by
// bootstrap directly. Loading them here ensures module-init wiring
// fires.
import "./policy-builder.js";
import "./priority-builder.js";
// Cross-page handlers for Policy / Decisions / Agents / Spend pages:
// reload, simulate, filter chips, builder deep-links, override-save,
// per-row buttons. All wired via document-level delegation at module
// init; no exports consumed by bootstrap.
import "./policy-handlers.js";
// daemon-settings.js owns the daemon-mode Settings panel data layer
// + renderer + the HMAC decrypt-status row. Bootstrap calls
// updateDecryptStatus from the HMAC import flow and showSettingsToast
// from the HMAC save handler.
import {
  showSettingsToast,
  updateDecryptStatus,
} from "./daemon-settings.js";
import { getWhoami, setWhoami, patchWhoami } from "./whoami.js";

// fetchFullEventById fetches one event by integer row id and returns it in the
// dashboard shape, or null on miss/failure. The local daemon returns a row with
// an already-decrypted `payload`; the cloud coordinator returns the raw row with
// an encrypted `payload_hint`, so on hosted we run it through decryptEventRow
// (the same mapper the list feed uses) to resolve Payload + Resource. A 404 from
// an older coordinator that lacks the route resolves to null → callers fall back
// to the thin row, no error surfaced.
async function fetchFullEventById(numId) {
  try {
    const ev = await api("/v1/events/by-id/" + encodeURIComponent(numId));
    if (!ev || (ev.id == null && ev.EventID == null)) return null;
    // Cloud rows arrive with payload_hint and no decrypted Payload; decrypt.
    // Local rows already carry the decrypted payload — pass through untouched.
    if (!isLocalDaemonMode()) return await decryptEventRow(ev);
    return ev;
  } catch {
    return null;
  }
}

// clipboardSafe neutralizes a string before it goes to the clipboard from a
// click-to-copy affordance. Event payload values (agent ids, branch names,
// resources, arbitrary payload keys) are attacker-influenceable, and "copy this
// event" is explicitly a paste-into-something action, so:
//   1. Strip C0/C1 control chars + DEL (keep \t and \n, the legit field/line
//      separators) so a pasted blob can't emit ANSI escapes or spoof extra log
//      lines in a terminal.
//   2. Prefix a single quote to any LINE that begins with a spreadsheet formula
//      trigger (= + - @) so a pasted cell is treated as text, not a formula
//      (CSV/formula injection, CWE-1236). Per-line because each "Label: value"
//      line becomes its own row/cell on paste.
// Operates on the clipboard string only — the on-screen escaped HTML is untouched.
function clipboardSafe(s) {
  // Drop C0 (except \t \n), DEL, and C1 controls. \x09=tab \x0A=LF are kept.
  // eslint-disable-next-line no-control-regex
  const noCtrl = String(s == null ? "" : s).replace(/[\x00-\x08\x0B-\x1F\x7F-\x9F]/g, "");
  return noCtrl
    .split("\n")
    .map((line) => (/^[=+\-@]/.test(line) ? "'" + line : line))
    .join("\n");
}

// flashCopied gives transient "copied ✓" feedback on a click-to-copy element
// WITHOUT mutating its textContent (which would destroy a kv row's structure or
// the button label). It adds a `.copied` class for ~1.1s; CSS swaps in the
// affordance (a checkmark / "copied" overlay). Idempotent under rapid clicks —
// a fresh timer replaces the prior one via a dataset-stored handle.
function flashCopied(el) {
  if (!el) return;
  el.classList.add('copied');
  const prev = el._copiedTimer;
  if (prev) clearTimeout(prev);
  el._copiedTimer = setTimeout(() => {
    el.classList.remove('copied');
    el._copiedTimer = null;
  }, 1100);
}

// rerenderEventSurfaces re-renders whichever event-bearing page is active so a
// row's expanded/collapsed state shows immediately after a click. The shared
// eventRowHTML reads the expand set, so re-rendering the active surface is all
// that's needed.
function rerenderEventSurfaces() {
  switch (state.activePage) {
    case 'events':
      renderEvents();
      break;
    case 'overview':
      renderOverviewEvents();
      break;
    case 'chats':
      renderChatsPage();
      break;
    case 'protection':
      // The Protection page's flagged-event feed reuses eventRowHTML, so a
      // row-expand toggle must re-render the feed here too — without this case
      // clicking a flagged row toggled the shared expand-set but nothing
      // repainted until the next poll tick.
      renderFlaggedFeed();
      break;
    default:
      // Other surfaces (worktrees/analytics) re-render on their own tick; a
      // no-op here is fine — the expand applies on their next render.
      break;
  }
}

/* ============================================================================
   AGENTCOLLISION DASHBOARD -- JS layer
   ============================================================================ */
export function setupLegacyDashboard() {
  'use strict';

  // PR #8 unification: read API base from config.js. In cloud-pages mode this
  // is the absolute coordinator URL; in local-daemon and coordinator-hosted
  // modes it's "" (same-origin, daemon serves /v1/ on its own port).
  const COORDINATOR = (window.AC_CONFIG && window.AC_CONFIG.API_BASE) || '';

  // v0.32.2: tag <body> with deploy-mode class so the
  // `.deploy-local-daemon .cloud-only { display: none }` CSS rule
  // (dashboard.css:2354) fires. Pre-fix the rule was authored but
  // nothing applied the class, leaving cloud-only buttons (Billing,
  // Team admin actions, BU switcher, Roles nav, Trial banner) visible
  // and clickable in solo mode where they'd 404 or no-op on the
  // backend. The .cloud-only markers in index.html (team-switcher,
  // bu-switcher, roles nav, setup-banner, decrypt-banner, trial-
  // ending-banner, team-roles-panel, the 4 cloud-only Account
  // panels) all activate via this single class flip.
  const deployMode =
    (window.AC_CONFIG && window.AC_CONFIG.DEPLOY_MODE) || "";
  if (deployMode === "local-daemon" && document.body) {
    document.body.classList.add("deploy-local-daemon");
    loadRealConflictsSince();
  }

  // ============================================================================
  // Route short-circuit: /account/confirm-delete?token=... is the email-link
  // confirmation page for account deletion. It's completely standalone — no
  // auth required (the token is the auth), no dashboard shell, no Supabase
  // hydration. Runs synchronously at script start so a stolen session can't
  // accidentally hit this flow; the POST only succeeds with a valid token.
  // ============================================================================
  if (typeof window !== 'undefined' && window.location && window.location.pathname === '/account/confirm-delete') {
    (async function runConfirmDelete() {
      const screen = document.getElementById('confirm-delete-screen');
      const titleEl = document.getElementById('confirm-delete-title');
      const bodyEl = document.getElementById('confirm-delete-body');
      const actionsEl = document.getElementById('confirm-delete-actions');
      const authScreen = document.getElementById('auth-screen');
      const sidebar = document.querySelector('.sidebar');
      const main = document.querySelector('.content') || document.querySelector('main');
      if (authScreen) authScreen.style.display = 'none';
      if (sidebar) sidebar.style.display = 'none';
      if (main) main.style.display = 'none';
      if (screen) screen.style.display = 'flex';

      const params = new URLSearchParams(window.location.search);
      const token = params.get('token') || '';
      if (!token) {
        titleEl.textContent = 'Invalid link';
        bodyEl.textContent = 'This confirmation link is missing its token.';
        return;
      }

      try {
        const r = await fetch(COORDINATOR + '/v1/user/delete-confirm/' + encodeURIComponent(token), { method: 'POST' });
        const data = await r.json().catch(() => ({}));
        if (!r.ok) {
          if (r.status === 410) {
            titleEl.textContent = 'Link expired';
            bodyEl.textContent = data.message || 'This confirmation link has expired or already been used. Request a new one from your dashboard.';
          } else if (r.status === 404) {
            titleEl.textContent = 'Invalid link';
            bodyEl.textContent = 'We couldn\u2019t verify this confirmation link. Request a new one from your dashboard.';
          } else {
            titleEl.textContent = 'Could not confirm deletion';
            bodyEl.textContent = data.message || data.error || ('Server error (' + r.status + ').');
          }
          actionsEl.innerHTML = '<a href="/" style="display: inline-block; margin-top: 12px; padding: 10px 20px; background: var(--accent); color: var(--text-inverse); text-decoration: none; border-radius: 8px; font-weight: 500; font-size: 14px;">Back to dashboard</a>';
          return;
        }
        const scheduledDate = data.scheduled_deletion
          ? new Date(data.scheduled_deletion).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
          : '30 days from now';
        titleEl.textContent = 'Account scheduled for deletion';
        bodyEl.innerHTML = 'Your account will be permanently deleted on <strong>' + scheduledDate + '</strong>. You have 30 days to cancel by signing back in.<br><br>All of your active sessions have been signed out.';
        actionsEl.innerHTML = '<a href="/" style="display: inline-block; margin-top: 12px; padding: 10px 20px; background: var(--accent); color: var(--text-inverse); text-decoration: none; border-radius: 8px; font-weight: 500; font-size: 14px;">Home</a>';
      } catch (e) {
        titleEl.textContent = 'Network error';
        bodyEl.textContent = 'Could not reach the coordinator. Check your connection and try again.';
        actionsEl.innerHTML = '<button onclick="location.reload()" style="margin-top: 12px; padding: 10px 20px; background: var(--accent); color: var(--text-inverse); border: none; border-radius: 8px; font-weight: 500; font-size: 14px; cursor: pointer;">Retry</button>';
      }
    })();
    return; // don't run the rest of the IIFE for this route
  }


  /**
   * Incrementally prepend new event rows to the list container.
   * Avoids the flash caused by replacing the entire innerHTML.
   * Returns the number of rows prepended.
   */

  // Hosted dashboard: coordinator only has /v1/sync/state, /v1/teams/info,
  // /v1/teams/members, and /v1/teams/audit. No /v1/info, /v1/leases,
  // /v1/events, /v1/activity, or /v1/config.
  // In-flight singleton: concurrent fetchAll() callers (boot,
  // refresh button, 'r' keypress, the auth-retry setTimeout) share
  // the same promise. Without this guard, two concurrent invocations
  // could both bump fetchAll._authRetries on the same module-global,
  // burning real retry budget on duplicate work — and a fast caller
  // could see state.loading flip false (finally) while a slower one
  // was still running, before any data landed.
  let fetchAllInflight = null;
  async function fetchAll() {
    if (fetchAllInflight) return fetchAllInflight;
    fetchAllInflight = _fetchAllImpl();
    try {
      return await fetchAllInflight;
    } finally {
      fetchAllInflight = null;
    }
  }
  async function _fetchAllImpl() {
    try {
      // All seven requests start together — same parallelism as the
      // Promise.all this replaced — but the first paint no longer BLOCKS on
      // all seven.
      //
      // Every endpoint answers in ~200ms individually, yet the page showed
      // nothing until the SLOWEST of seven resolved, and during that window
      // the Chats page rendered its empty state. Measured 2026-08-28:
      // info / sync-state / heartbeats ~0.2s each; audit and analytics are
      // the stragglers, and neither is load-bearing for the view people open
      // first.
      //
      // So: await only the three the chat list actually needs, paint, then
      // fold in the rest as they land. Every promise is created BEFORE the
      // first await, so nothing is serialized by this change.
      const pTeamInfo = api('/v1/teams/info');
      const pMembers = api('/v1/teams/members');
      const pLeaseState = api('/v1/sync/state');
      const pAudit = api('/v1/teams/audit?limit=100').catch(() => ({ entries: [] }));
      const pEvents = api(withBuFilter('/v1/teams/events?limit=200')).catch(() => ({ events: [] }));
      // New: per-agent PreToolUse heartbeats (Read/Grep/Bash/Edit/Write).
      // Lets the dashboard render "Browsing"/"Reading" status the way the
      // local UI does. NO FALLBACK — if it fails the merge step skips it.
      const pHeartbeats = api('/v1/teams/heartbeats').catch(() => ({ heartbeats: [] }));
      // 30-day analytics: drives the "Collisions prevented" hero stat.
      // Cheap (single rolled-up query, cached coordinator-side); fail-soft
      // because the metric is informational, not load-bearing.
      // NOTE: must be "30d" not "30" — getTeamAnalytics matches the
      // literal "30d"/"90d" and otherwise falls back to 7 days, so the
      // old "?window=30" silently fetched a 7-day window despite the
      // "30-day" intent (fixed in the metric-reconciliation pass).
      const pAnalytics = api('/v1/teams/analytics?window=30d').catch(() => null);

      // FIRST PAINT: identity + leases + heartbeats is exactly what the chat
      // list, the sidebar badges and the lease/file pages read.
      const [teamInfo, leaseState, heartbeats] = await Promise.all([
        pTeamInfo,
        pLeaseState,
        pHeartbeats,
      ]);
      state.teamInfo = teamInfo;
      state.info = { version: 'team', hostname: teamInfo.team_name || '', user: '' };
      // Decrypt before storing: the raw rows carry an AES-GCM display_hint,
      // and painting them undecrypted would flash ciphertext where the chat
      // name goes. Same decrypt the tail does below; doing it here means the
      // first paint is already correct rather than merely early.
      const firstHeartbeats = await Promise.all(
        ((heartbeats && heartbeats.heartbeats) || []).map(decryptHeartbeat),
      );
      state.heartbeats = firstHeartbeats;
      state.deviceLabels = buildDeviceLabelMap(firstHeartbeats, state.deviceLabels);
      const firstLeases = await Promise.all(withoutLegacyTwins(leaseState.leases).map(decryptLeaseRow));
      if (!state.overviewOwnsLeases) {
        state.leases = firstLeases.filter((l) => l.State === 'held');
        state.leasesAll = firstLeases;
      }
      // Clear the one-shot loading flag HERE, not after the slow tail —
      // otherwise the skeleton keeps shimmering over data we already have.
      state.loading = false;
      markTeamReachable();
      setConnected(true);
      renderAll();

      // Then the tail. These only enrich what is already on screen (member
      // names, the events panel, the analytics hero stat); the existing code
      // below re-renders once they arrive.
      const [members, audit, events, analytics] = await Promise.all([
        pMembers,
        pAudit,
        pEvents,
        pAnalytics,
      ]);
      state.analytics = analytics;
      // Leases were already decrypted and stored for the first paint above
      // (decryptLeaseRow swaps the hashed Resource for its decrypted display
      // hint when an HMAC key is loaded, so chat labels and file lists render
      // real paths). Reuse that result rather than decrypting the same rows a
      // second time — it is the most expensive step in this function.
      const allLeases = state.leasesAll;
      // Merge two feeds for the Events panel:
      //   1. coordination_events: lease.acquired/blocked/expired etc. (the
      //      live activity dashboard users actually care about)
      //   2. audit_log: device-auth, team.created, role changes (admin trail)
      const coordEvents = await Promise.all((events.events || []).map(decryptEventRow));
      // Audit actions already have a dotted namespace (e.g. "team.created",
      // "member.renamed", "billing.checkout_started"). The prior code
      // unconditionally prepended "team." — which produced "team.team.X"
      // double-prefixes for any action whose namespace was already "team."
      // and made non-team rows like "billing.checkout_started" look like
      // team events. Use the action verbatim.
      //
      // Audit rows also don't carry an agent_id (they're user actions, not
      // chat-session actions), so we leave AgentID empty — eventRowHTML
      // renders the agent-context column blank in that case. `target` is
      // typically a user_id or resource ID; we surface it via Resource
      // (the renderer's "detail" column). actor_id (e.g. "user:UUID") is
      // surfaced via the Payload so the row renderer can show "by <actor>"
      // when it wants. We do NOT cram actor_id into AgentID like the old
      // code did — that's what produced the duplicate-UUID columns in the
      // Events page screenshot.
      const auditEvents = (audit.entries || []).map(e => ({
        EventType: e.action || 'audit.unknown',
        event_type: e.action || 'audit.unknown',
        OccurredAt: e.created_at, occurred_at: e.created_at,
        Resource: e.detail || e.target || '',
        resource: e.detail || e.target || '',
        // No agent_id — these are admin/audit rows, not chat actions.
        AgentID: '', agent_id: '',
        EventID: 'a' + e.id, event_id: 'a' + e.id,
        Payload: { actor_id: e.actor_id || '', target: e.target || '', detail: e.detail || '' },
        payload: { actor_id: e.actor_id || '', target: e.target || '', detail: e.detail || '' },
      }));
      state.events = [...coordEvents, ...auditEvents].sort((a, b) =>
        new Date(b.occurred_at).getTime() - new Date(a.occurred_at).getTime()
      );
      state.eventsLoaded = true;
      // Synthesize lease history (released/expired/revoked rows) from the
      // event stream so the Leases panel's filter chips work. Coordinator's
      // /v1/sync/state only returns currently-held leases — without this
      // step "All", "Expired", "Released" filters always show empty.
      if (!state.overviewOwnsLeases) state.leasesAll = mergeLeaseHistoryFromEvents(allLeases, state.events);

      // Stale-HMAC-key self-heal (issue #179). The three bootstrap paths
      // prefer a cached localStorage key over a fresh daemon import, so if
      // the team rotated its HMAC key the cached one silently fails to
      // decrypt and the dashboard shows hashed wire form with no clue why.
      // Probe ONCE per session: if a key is loaded but a known-encrypted
      // field won't decrypt, the key is stale → pull a fresh one from the
      // daemon. Guarded by a flag so it never loops, and only runs when we
      // actually have both a key and an encrypted sample to test.
      if (!state._hmacProbed && state.hmacKey) {
        const sample = (state.events || []).find((e) => e && e.payload_hint);
        if (sample) {
          state._hmacProbed = true;
          decryptHint(sample.payload_hint)
            .then((pt) => {
              if (pt === null) {
                // Loaded key can't decrypt a real field → stale. Re-import.
                tryAutoImportHmacKey()
                  .then(() => updateDecryptBanner())
                  .catch(() => {});
              }
            })
            .catch(() => {});
        }
      }

      // Decrypt the per-agent activity heartbeats. The display_hint plaintext
      // produced by the daemon's encryptDisplayHint() is "<file>\n<tool>"
      // (with file possibly empty for tool-only signals like Bash). We split
      // back out so aggregateChats can render them. If decryption fails (no
      // hmacKey, or hint missing), lastFile/lastTool stay empty — the chat
      // still appears, just without rich detail.
      //
      // device_hint (added v0.14.5) carries an encrypted "user@host" device
      // label so the chat row can render "claude-code · vincentsgro@Mac"
      // instead of "claude-code · ?". Indexed into state.deviceLabels by
      // laptop_id; the chat-row renderer reads it as a fallback when no
      // member display name is set.
      const rawHeartbeats = heartbeats.heartbeats || [];
      const decryptedHeartbeats = await Promise.all(rawHeartbeats.map(decryptHeartbeat));
      state.heartbeats = decryptedHeartbeats;
      state.deviceLabels = buildDeviceLabelMap(decryptedHeartbeats, state.deviceLabels);
      state.activeAgents = (members.members || []).filter(m => m.is_online).map(m => ({
        agent_id: m.laptop_id, last_seen: m.last_seen, last_file: '', last_tool: '',
      }));
      state.members = members.members || [];
      // people[] groups members by user_id and lists their machines.
      // The lease table's "User" column resolves laptop_id → email
      // through this list — see memberName(). Pre-fix the column
      // showed prettifyHostname(member.display_name) which was just
      // the bare auto-registered hostname ("Mac.lan" → "Mac").
      state.people = members.people || [];
      // Admins can request a remote revoke via POST /v1/teams/leases/{id}/revoke.
      // The daemon owning the lease picks it up on its next pull tick (~5s)
      // and calls lease.Revoke locally. Members see no Release button.
      //
      // In local-daemon mode the user is the daemon operator, so the gate is
      // the daemon's own allow_revoke setting: with it off the daemon refuses
      // every release, and the button would only ever fail.
      const localDaemonModeRevoke =
        window.AC_CONFIG && window.AC_CONFIG.DEPLOY_MODE === "local-daemon";
      state.allowRevoke = localDaemonModeRevoke
        ? await api('/v1/config').then((c) => c.allow_revoke === true, () => false)
        : (teamInfo && teamInfo.your_role === 'admin');
      state.loading = false;
      // Reset the auth-retry counter on success so a later
      // session-expiry can retry fresh instead of staying capped
      // at the 3-attempt ceiling.
      fetchAll._authRetries = 0;
      setConnected(true);
      renderAll();
      // Probe the local daemon (if any) so the next render of the
      // setup banner can pick the right mode. Doesn't block fetchAll;
      // banner re-renders on completion.
      //
      // v0.31.9: skip in local-daemon mode — we ARE the local daemon
      // (the dashboard is served by it). probeLocalDaemon hits
      // /v1/team/list which is loopback-token-protected and 403s for
      // origin-less browser fetches; on a port-scan miss it spam-
      // errors the console for ports 7778..7790. Setup banner doesn't
      // apply in local-daemon mode anyway (FEATURES.setup_banner =
      // false).
      const localDaemonModeBootstrap =
        window.AC_CONFIG && window.AC_CONFIG.DEPLOY_MODE === "local-daemon";
      if (!localDaemonModeBootstrap) {
        probeLocalDaemon().then(updateSetupBanner);
      }
    } catch (e) {
      if (e.message === 'unauthorized') {
        // Auth race on cold load. Schedule a bounded retry; flip
        // loading immediately (see finally block) so the UI shows
        // its real state instead of perpetual skeleton.
        fetchAll._authRetries = (fetchAll._authRetries || 0) + 1;
        if (fetchAll._authRetries <= 3) {
          setTimeout(() => fetchAll(), 800 * fetchAll._authRetries);
        }
      } else if (isLocalDaemonMode()) {
        // The daemon is up (it served this page); only its team server is
        // not answering. Retry with backoff, and tick anyway so pages fed by
        // local data render instead of waiting on this fetch.
        console.warn('fetchAll: team server unreachable', e);
        markTeamUnreachable(e, fetchAll);
        state.loading = false;
        renderAll();
      } else {
        console.warn('fetchAll failed', e);
        setConnected(false);
      }
    } finally {
      // state.loading is a one-shot "boot fetch has completed at
      // least once" signal. Always flip it off here, even on the
      // auth-retry path — perpetual `true` blocked rendering on
      // older revisions (PR #238 removed the panel guards that
      // depended on it; PR #234 stopped relying on it; this finally
      // exists for any future callers that resurrect the flag).
      state.loading = false;
    }
  }

  // refreshAudit — pull /v1/teams/audit and merge into state.events
  // alongside the SSE-delivered coordination events. Coord events
  // (id: numeric) flow through SSE; audit events (id: "a<n>") are
  // admin-action rows that need a periodic refresh because they're
  // NOT on the SSE channel.
  //
  // 5-min cadence is fine — audit rows fire on user actions (rename,
  // role CRUD, etc.), not background loops, so a few minutes of
  // staleness is acceptable. Cost class: 0.2 req/min/user.
  async function refreshAudit() {
    try {
      const audit = await api('/v1/teams/audit?limit=100');
      const entries = audit.entries || [];
      const auditEvents = entries.map((e) => ({
        EventType: e.action || 'audit.unknown',
        event_type: e.action || 'audit.unknown',
        OccurredAt: e.created_at, occurred_at: e.created_at,
        Resource: e.detail || e.target || '',
        resource: e.detail || e.target || '',
        AgentID: '', agent_id: '',
        EventID: 'a' + e.id, event_id: 'a' + e.id,
        Payload: { actor_id: e.actor_id || '', target: e.target || '', detail: e.detail || '' },
        payload: { actor_id: e.actor_id || '', target: e.target || '', detail: e.detail || '' },
      }));
      // Merge with existing state.events: keep coord events (id !== 'a*')
      // from SSE, replace the audit pool with the fresh fetch.
      const coordEvents = (state.events || []).filter((ev) => {
        const id = ev.EventID || ev.event_id || '';
        return typeof id === 'string' && !id.startsWith('a');
      });
      state.events = [...coordEvents, ...auditEvents].sort((a, b) =>
        new Date(b.occurred_at).getTime() - new Date(a.occurred_at).getTime()
      );
    } catch (_e) {
      // Soft fail — audit is informational, not load-bearing.
    }
  }

  // Daemons push each lease twice while lease keys migrate; the '#legacy'
  // twin is the same lease, so keeping it doubled every lease count.
  function withoutLegacyTwins(rows) {
    return (rows || []).filter((l) => !String(l.lease_id || '').endsWith('#legacy'));
  }

  async function refreshLeaseData() {
    // Events fetch is conditional on SSE NOT being active. The split:
    //   - Cloud Team/Enterprise: SSE keeps state.events fresh (poll skips events)
    //   - Cloud Free tier: SSE gated off, so poll fills the gap
    //   - Local-daemon mode: no SSE endpoint, so poll fills the gap
    // SSE handler has its own id-dedupe merge; running both would
    // conflict. Mirror openCloudStream's gate in overview.js's start().
    const localDaemon =
      window.AC_CONFIG && window.AC_CONFIG.DEPLOY_MODE === "local-daemon";
    const sseActive = !localDaemon && isSSEEnabled();
    try {
      const fetches = [
        api('/v1/sync/state'),
        api('/v1/teams/members'),
        api('/v1/teams/heartbeats').catch(() => ({ heartbeats: [] })),
      ];
      if (!sseActive) {
        // Limit to 200 to match fetchAll's initial budget. The events
        // panel only renders top 8 anyway; we just need recency.
        fetches.push(api(withBuFilter('/v1/teams/events?limit=200')).catch(() => ({ events: [] })));
      }
      const results = await Promise.all(fetches);
      const [leaseState, members, heartbeats] = results;
      const eventsResp = sseActive ? null : results[3];

      const allLeases = await Promise.all(withoutLegacyTwins(leaseState.leases).map(decryptLeaseRow));
      if (!state.overviewOwnsLeases) {
        state.leases = allLeases.filter(l => l.State === 'held');
        state.leasesAll = allLeases;
      }
      state.members = members.members || [];
      state.people = members.people || [];
      state.activeAgents = (members.members || []).filter(m => m.is_online).map(m => ({
        agent_id: m.laptop_id, last_seen: m.last_seen, last_file: '', last_tool: '',
      }));
      const rawHeartbeats = heartbeats.heartbeats || [];
      const decryptedHeartbeats = await Promise.all(rawHeartbeats.map(decryptHeartbeat));
      state.heartbeats = decryptedHeartbeats;
      state.deviceLabels = buildDeviceLabelMap(decryptedHeartbeats, state.deviceLabels);

      // Refresh coord events when SSE isn't carrying that load.
      // Merge with existing audit rows so refreshAudit's work isn't
      // clobbered (audit rows have EventID starting with "a"; coord
      // rows start with "c"). Same shape pattern fetchAll uses.
      if (eventsResp) {
        const coordEvents = await Promise.all((eventsResp.events || []).map(decryptEventRow));
        const auditRows = (state.events || []).filter((ev) => {
          const id = ev.EventID || ev.event_id || '';
          return typeof id === 'string' && id.startsWith('a');
        });
        state.events = [...coordEvents, ...auditRows].sort((a, b) =>
          new Date(b.occurred_at || 0).getTime() - new Date(a.occurred_at || 0).getTime()
        );
        state.eventsLoaded = true;
      }

      if (!state.overviewOwnsLeases) state.leasesAll = mergeLeaseHistoryFromEvents(state.leases, state.events || []);
      setConnected(true);
      // The team server is back: let the boot fetch fill what it missed.
      if (isTeamUnreachable()) retryTeamNow(fetchAll);
    } catch (e) {
      if (e.message === 'unauthorized') return;
      if (isLocalDaemonMode()) {
        markTeamUnreachable(e, fetchAll);
        updateConnectionPill();
      } else {
        setConnected(false);
      }
    }
  }


  async function refreshLeases() {
    await refreshLeaseData();
    updateSidebarBadges();
    updateHero();
    // Re-check the "Almost there. Install AgentCollision" banner on every
    // poll. Pre-2026-04-26 the banner was set once from fetchAll() and
    // never re-evaluated after state.members updated, so a daemon that
    // checked in seconds after page load left the banner stuck forever.
    // probeLocalDaemon is best-effort; both calls update the same
    // banner state so order doesn't matter.
    //
    // v0.31.10: skip probeLocalDaemon in local-daemon mode for the
    // same reason fetchAll skips it — we ARE the local daemon, and
    // /v1/team/list 403s for origin-less browser fetches. Pre-fix
    // this hit on every refresh tick, spamming the console.
    const localDaemonRefresh =
      window.AC_CONFIG && window.AC_CONFIG.DEPLOY_MODE === "local-daemon";
    if (!localDaemonRefresh) {
      probeLocalDaemon().then(updateSetupBanner);
    }
    updateSetupBanner();
  }


  function setConnected(c) {
    // v0.32.4: don't bail early when state.connected already matches.
    // overview.js:fetchCountsOnce assigns `state.connected = true`
    // directly (bypassing this setter) when local-daemon-mode counts
    // come back 200. If that assignment lands BEFORE bootstrap's
    // fetchAll-success path calls setConnected(true), this function
    // would bail without clearing the `.disconnected` class the HTML
    // ships with by default — the pill would render red ("daemon
    // online" text but disconnected style). Sync the DOM uniformly.
    state.connected = c;
    const pill = $('conn-pill');
    if (pill) pill.classList.toggle('disconnected', !c);
    // Labeling: the text reflects whether any daemon has reported in, not just API reachability
    updateConnectionPill();
  }


  function renderInfo() {
    if (!state.info) return;
    $('brand-version').textContent = 'Team Dashboard';
  }

  // setBadge writes a sidebar badge that is honest about its SCOPE.
  //
  // These badges count what is live RIGHT NOW, while the pages beside them
  // list a full hour of history. That mismatch made the dashboard look
  // broken (2026-08-28): the Chats page showed four cards each reading
  // "99 edits", right next to a "Chats 0" badge — and BOTH were correct.
  // Nothing on screen said they measured different things.
  //
  // So when live and total differ, render "live/total" and explain the
  // split in the tooltip. When they agree (or no total is known) it stays a
  // single number — the common case shouldn't get noisier.
  function setBadge(id, live, total, what) {
    const el = $(id);
    if (!el) return;
    // Don't assert "0" before the boot fetch has ever completed. A zero the
    // user can't distinguish from a real one is the same lie the chats page
    // used to tell with its empty state; show a neutral placeholder until
    // data actually lands.
    if (state.loading) {
      el.textContent = '·';
      el.title = 'Loading…';
      return;
    }
    const hasTotal = typeof total === 'number' && total > live;
    el.textContent = hasTotal ? live + '/' + total : String(live);
    el.title = hasTotal
      ? live + ' ' + what + ' now · ' + total + ' in the last hour'
      : live + ' ' + what + ' now';
  }

  function updateSidebarBadges() {
    // Leases: held == live. state.leasesAll is the whole window, so a chat
    // that edited and released still shows on the page while this reads 0.
    setBadge(
      'nav-leases-count',
      state.leases.length,
      (state.leasesAll || []).length,
      'held',
    );
    // Files: distinct paths. Live = currently under a held lease; total =
    // every path touched in the window (what renderFilesPage lists).
    const distinct = (rows) => new Set((rows || [])
      .map((l) => friendlyResource(l.Resource || ''))
      .filter((p) => p)).size;
    setBadge(
      'nav-files-count',
      distinct(state.leases),
      distinct(state.leasesAll),
      'files being edited',
    );
    // Count LIVE chats, not every agent that touched a lease in the last
    // hour. aggregateChats() returns everything active-in-the-window,
    // which on a machine running many parallel worktrees is dozens of
    // already-finished sessions; the badge should reflect "what's running
    // NOW" — a chat currently holding a lease (held > 0) or with a fresh
    // heartbeat (browsing). The Chats PAGE still lists the full hour, so
    // nothing is hidden; the badge now shows live/total so the difference
    // is visible instead of looking like a broken counter.
    const allChats = aggregateChats();
    const liveChats = allChats.filter(
      (c) => (c.held || 0) > 0 || c.browsing,
    );
    setBadge('nav-chats-count', liveChats.length, allChats.length, 'chats active');
  }


  // Phase 1 (v2): selected decision pill. "all" shows every event;
  // any other value filters to events whose event_type matches.
  // State lives on `state` because navigate() resets state.activePage
  // — keeping the pill state separate would mean the user's filter
  // gets nuked on tab switch. Persistence beyond the session is
  // explicitly NOT a goal: the pill is "what am I looking at right
  // now," not a saved preference.
  if (state.overviewPill === undefined) {
    state.overviewPill = 'all';
  }


  // Phase 1 (v2): poll /v1/events/counts every 30s and stamp the
  // counts onto the pill labels. The endpoint is cheap (in-process
  // histogram aggregation) and the cadence is conservative — the
  // counts are informational, not load-bearing.
  //
  // Daemon-only: the cloud coordinator doesn't expose
  // /v1/events/counts (it tracks team-coordinator events, not
  // local lease events). Cloud-mode users still see the pills as
  // filters but without count badges — better UX than letting
  // every dashboard refresh log a 404 in their browser console.
  let _lastDecisionCountsAt = 0;
  async function refreshDecisionCounts() {
    const cfg = window.AC_CONFIG;
    if (cfg && cfg.DEPLOY_MODE !== 'local-daemon') return;
    try {
      const data = await api('/v1/events/counts?since=24h');
      const byType = (data && data.by_event_type) || {};
      const total = data && typeof data.total === 'number' ? data.total : 0;
      const setCount = (key, n) => {
        const el = document.querySelector('[data-count-for="' + key + '"]');
        if (el) el.textContent = n > 999 ? '999+' : String(n);
      };
      setCount('all', total);
      ['lease.acquired', 'lease.released', 'lease.blocked', 'lease.preempted', 'lease.expired']
        .forEach((t) => setCount(t, byType[t] || 0));
      _lastDecisionCountsAt = Date.now();
    } catch (e) {
      // Silent on failure — counts are decoration.
    }
  }

  // Wire pill clicks. Delegated handler on the parent div so the
  // pills can be re-rendered without losing the listener.
  document.addEventListener('click', (e) => {
    const pill = e.target && e.target.closest && e.target.closest('.decision-pill');
    if (!pill) return;
    const wrap = pill.closest('#overview-decision-pills');
    if (!wrap) return;
    const which = pill.dataset.pill;
    if (!which) return;
    state.overviewPill = which;
    // Visually mark the active pill. Plain class toggling — the
    // styling lives in the inline `style` attribute today; future
    // refactor could move this to dashboard.css.
    wrap.querySelectorAll('.decision-pill').forEach((p) => {
      const active = p.dataset.pill === which;
      p.classList.toggle('active', active);
      p.style.background = active ? 'var(--accent-bg)' : 'var(--bg-elevated)';
      p.style.borderColor = active ? 'var(--accent)' : 'var(--border)';
      p.style.color = active ? 'var(--accent)' : 'var(--text-secondary)';
      p.style.fontWeight = active ? '600' : 'normal';
    });
    // Re-render the event list with the new filter.
    if (state.activePage === 'overview') renderOverviewEvents();
  });

  // Install the browser back/forward listener once during setup.
  installPopstateHandler();

  // Install the global tooltip widget's mouseover/mouseout listeners.
  // Idempotent — safe to call once per session.
  installTooltip();

  setupActivityLines();


  function renderAll() {
    renderInfo();
    updateSidebarBadges();
    updateSetupBanner();
    updateDecryptBanner();
    updateConnectionPill();
    // Notify page modules (src/pages/*) that fresh data has arrived.
    // Each listening module re-renders if its section is active.
    document.dispatchEvent(new CustomEvent('dashboard:tick'));
  }

  // Setup banner interactions. The per-command Copy buttons use a generic
  // data-copy-target="<element-id>" attribute so the banner can have as many
  // copy-able snippets as it wants without the JS needing to know their ids.
  document.addEventListener('click', (e) => {
    // #setup-banner-dismiss + #decrypt-banner-dismiss handlers live
    // in src/banners.js (Phase K Tier 2m). Switch + import + settings
    // branches stay here because they cross into bootstrap helpers.
    if (e.target.id === 'setup-switch-btn') {
      // One-click switch to the team this dashboard is viewing.
      const tid = state.teamInfo && state.teamInfo.team_id;
      if (!tid) return;
      const btn = e.target;
      btn.disabled = true; const prev = btn.textContent; btn.textContent = 'Switching…';
      switchLocalDaemonTeam(tid).finally(() => {
        btn.disabled = false; btn.textContent = prev;
      });
      return;
    }
    if (e.target.id === 'decrypt-banner-settings') {
      // IA arc B''' (2026-05-27): Decryption panel moved from
      // Settings/Daemon to the Machines page.
      navigate('machines');
      return;
    }
    if (e.target.id === 'decrypt-banner-import') {
      const btn = e.target;
      const prev = btn.textContent;
      btn.disabled = true; btn.textContent = 'Importing…';
      (async () => {
        const ok = await tryAutoImportHmacKey();
        btn.disabled = false; btn.textContent = prev;
        if (ok) {
          showToast('success', 'Imported the team key from this computer. File names will now show in full.');
          updateDecryptBanner();
        } else if (state.localNetworkBlocked) {
          showToast('error', 'Your browser blocked this page from reaching your computer. Click the icon left of the address bar, turn on Apps on device (Local network access in older Chrome), then reload.');
        } else {
          showToast('error', 'Could not reach local daemon. Is `agc daemon` running on this machine? Try `agc status` to verify.');
        }
      })();
      return;
    }
    const copyBtn = e.target.closest('[data-copy-target]');
    if (copyBtn) {
      const targetId = copyBtn.getAttribute('data-copy-target');
      const target = targetId && document.getElementById(targetId);
      if (target && target.textContent) {
        navigator.clipboard?.writeText(target.textContent.trim()).then(() => {
          copyBtn.textContent = 'Copied!';
          setTimeout(() => { copyBtn.textContent = 'Copy'; }, 1500);
        });
      }
      return;
    }
    // Generic value-copy: any [data-copy="<value>"] element copies its
    // attribute value on click and briefly flashes "copied". Used by the
    // visible id chips (event id, spend chat id, incident id) so a specific
    // row can be referenced without hovering for a tooltip.
    const valCopy = e.target.closest('[data-copy]');
    if (valCopy) {
      const val = valCopy.getAttribute('data-copy');
      if (val) {
        navigator.clipboard?.writeText(val).then(() => {
          const prev = valCopy.textContent;
          valCopy.textContent = 'copied';
          setTimeout(() => { valCopy.textContent = prev; }, 1000);
        });
      }
      return;
    }
    // Click-to-copy on expanded event detail: a kv row ([data-copy-kv]) copies its
    // full (untruncated) value; the "Copy event" button ([data-copy-event]) copies
    // the whole event as a "Label: value" block. Both flash a transient .copied
    // class (CSS swaps in a "copied ✓" affordance) instead of mutating textContent,
    // so the row/button markup survives. Stop propagation so the row-copy doesn't
    // also toggle the expansion closed.
    const kvCopy = e.target.closest('[data-copy-kv]');
    if (kvCopy) {
      e.stopPropagation();
      const val = clipboardSafe(kvCopy.getAttribute('data-copy-kv'));
      if (val && navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(val).then(() => flashCopied(kvCopy));
      }
      return;
    }
    const evCopy = e.target.closest('[data-copy-event]');
    if (evCopy) {
      e.stopPropagation();
      const val = clipboardSafe(evCopy.getAttribute('data-copy-event'));
      if (val && navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(val).then(() => flashCopied(evCopy));
      }
      return;
    }
  });
  // ----- event-row drill-in: click any event row to expand its full detail -----
  // Works on every surface that uses the shared eventRowHTML (Events, overview,
  // chats, worktrees, analytics) because they all render the same row. Guard out
  // the copy-id chip, the warning pills, and any in-row link/tooltip pill so they
  // keep their own behavior.
  document.addEventListener('click', (e) => {
    if (
      e.target.closest('[data-copy]') ||
      e.target.closest('[data-copy-kv]') ||
      e.target.closest('[data-copy-event]') ||
      e.target.closest('[data-page-link]') ||
      e.target.closest('a') ||
      e.target.closest('.event-warning-pill')
    )
      return;
    // NB: the copy handlers above live in a SEPARATE document-level click
    // listener, so their stopPropagation() does not stop THIS listener from
    // firing on the same click — the kv rows / Copy-event button render INSIDE
    // .event-row-clickable, so without these guards a copy click would also
    // toggle the expansion closed. Guard them here too.
    const row = e.target.closest('.event-row-clickable');
    if (!row) return;
    const eid = row.getAttribute('data-event-expand-id');
    if (!eid) return;
    const wasExpanded = eventIsExpanded(eid);
    toggleEventExpanded(eid);
    rerenderEventSurfaces();
    // On OPEN, fetch the authoritative full event by id so the detail shows the
    // complete payload — for events OLDER than the list window the in-memory row
    // is thin. The by-id route now exists on BOTH backends; fetchFullEventById
    // normalizes the shapes (cloud rows get decrypted via decryptEventRow).
    // numericEventId is "" for audit ("a"+id) events, which have no by-id source.
    if (!wasExpanded && !hasFullEvent(eid)) {
      const numId = numericEventId(eid);
      if (numId) {
        fetchFullEventById(numId).then((ev) => {
          if (ev) {
            cacheFullEvent(ev);
            // Re-check: a collapse during the fetch makes this a no-op.
            if (eventIsExpanded(eid)) rerenderEventSurfaces();
          }
          /* null → older coordinator w/o the route, or miss: keep the thin row */
        });
      }
    }
  });

  // ----- event handlers -----
  document.addEventListener('click', (e) => {
    const navBtn = e.target.closest('[data-page]');
    if (navBtn && navBtn.classList.contains('nav-item')) {
      // The sidebar always opens a page unfiltered; a number's filter only
      // applies when you arrive from that number.
      state.eventQuery = null;
      agentsState.only = null;
      navigate(navBtn.dataset.page);
      if (navBtn.dataset.page === 'events') renderEvents();
      if (navBtn.dataset.page === 'agents') renderAgentsPage();
      return;
    }

    const link = e.target.closest('[data-page-link]');
    if (link) {
      e.preventDefault();
      // Optional deep-link: data-event-id pre-filters the Events page to that
      // event so a collision row can jump straight to its event detail. We
      // prefix "#" so the Events search does an EXACT id match on the server
      // (id 3 → event 3, not 13/30), however old the event is.
      const eventId = link.dataset.eventId;
      if (link.dataset.pageLink === 'events') state.eventQuery = null;
      if (eventId) {
        const filterVal = '#' + eventId;
        state.eventFilterText = filterVal;
        state.eventPage = 0;
        const input = $('event-filter');
        if (input) input.value = filterVal;
      }
      navigate(link.dataset.pageLink);
      if (link.dataset.settingsTarget) openSettingsAt(link.dataset.settingsTarget);
      return;
    }

    const rel = e.target.closest('[data-release]');
    if (rel) { releaseLease(rel.dataset.release); return; }

    const sub = e.target.closest('[data-file-idx]');
    if (sub) { sub.classList.toggle('open'); return; }

    const chatToggle = e.target.closest('[data-chat-toggle]');
    if (chatToggle) {
      // Don't toggle if user is selecting text
      const sel = window.getSelection();
      if (sel && sel.toString().length > 0) return;
      const aid = chatToggle.dataset.chatToggle;
      if (state.expandedChats.has(aid)) state.expandedChats.delete(aid);
      else state.expandedChats.add(aid);
      renderChatsPage();
      return;
    }

    const chatPageBtn = e.target.closest('[data-chat-page]');
    if (chatPageBtn && !chatPageBtn.disabled) {
      if (chatPageBtn.dataset.chatPage === 'prev') state.chatPage = Math.max(0, state.chatPage - 1);
      else if (chatPageBtn.dataset.chatPage === 'next') state.chatPage++;
      renderChatsPage();
      return;
    }

    // Per-account filter chip on the Chats page (Team view). Empty
    // data-chat-member ("all") clears the filter; a member name narrows
    // the list to that person's chats. Resets pagination.
    const chatMemberChip = e.target.closest('[data-chat-member]');
    if (chatMemberChip) {
      state.chatMemberFilter = chatMemberChip.dataset.chatMember || '';
      state.chatPage = 0;
      renderChatsPage();
      return;
    }

    const eventChip = e.target.closest('[data-event-type]');
    if (eventChip) {
      document.querySelectorAll('[data-event-type]').forEach((c) => c.classList.remove('active'));
      eventChip.classList.add('active');
      state.eventTypeFilter = eventChip.dataset.eventType;
      // A new category has a different set of specific types — reset the
      // second-level sub-filter so we don't carry over a now-invalid type
      // (renderEvents → rebuildSubFilter also guards this, but resetting here
      // keeps the dropdown's selection honest the instant the chip is clicked).
      state.eventSubTypeFilter = 'all';
      state.eventPage = 0;
      renderEvents();
      return;
    }

    const chip = e.target.closest('[data-lease-state]');
    if (chip) {
      document.querySelectorAll('[data-lease-state]').forEach((c) => c.classList.remove('active'));
      chip.classList.add('active');
      state.leaseFilterState = chip.dataset.leaseState;
      renderLeasesPage();
      return;
    }

    // Repo chip on the leases page. Radio-style: clicking sets the
    // repo filter; clicking the "all" chip (empty value) clears it.
    const repoChip = e.target.closest('[data-lease-repo]');
    if (repoChip) {
      state.leaseFilterRepo = repoChip.dataset.leaseRepo || '';
      renderLeasesPage();
      return;
    }

    // Mine-only toggle on the leases page (v0.38.0 Phase 4b).
    // Flips state.leaseFilterMineOnly and triggers an immediate
    // re-render. Note this filters CLIENT-CACHED state.leases
    // — the next 5s fetchLeasesOnce tick re-issues with the new
    // ?actor_subject= query param, so the underlying data set
    // updates within ~5s. The re-render gives instant visual
    // feedback in the meantime (existing repos chip + state
    // filter applies on the already-cached set).
    const mineToggle = e.target.closest('[data-leases-mine-only]');
    if (mineToggle) {
      state.leaseFilterMineOnly = !state.leaseFilterMineOnly;
      renderLeasesPage();
      // Nudge an immediate fetch so the table reflects the new
      // filter without waiting for the next 5s tick.
      document.dispatchEvent(new CustomEvent('dashboard:refetch-leases'));
      return;
    }

  });

  document.addEventListener('input', (e) => {
    if (e.target.id === 'lease-filter') { state.leaseFilterText = e.target.value; renderLeasesPage(); }
    if (e.target.id === 'event-filter') { state.eventFilterText = e.target.value; state.eventPage = 0; renderEvents(); }
    if (e.target.id === 'chat-filter') { state.chatFilterText = e.target.value; state.chatPage = 0; renderChatsPage(); }
  });

  // Events second-level type dropdown — filters the list to one exact event
  // type within the active category. `change` is the canonical <select> event.
  document.addEventListener('change', (e) => {
    if (e.target.id === 'event-subfilter') {
      state.eventSubTypeFilter = e.target.value || 'all';
      state.eventPage = 0;
      renderEvents();
    }
  });


  async function releaseLease(leaseID) {
    if (!leaseID) return;
    // Members shouldn't see the button at all (renderLeasesPage gates
    // on state.allowRevoke), but defensively re-check here.
    if (!state.allowRevoke) {
      await showConfirm('Not allowed', 'Only team admins can release another member’s lease.', 'OK');
      return;
    }
    const ok = await showConfirm(
      'Release this lease?',
      'The computer holding this lease lets go on its next check-in, usually within 15 seconds. An edit in progress there may be interrupted.',
      'Release'
    );
    if (!ok) return;
    state.releasingLeases.add(leaseID);
    renderLeasesPage();
    try {
      await apiWithMessage('/v1/teams/leases/' + encodeURIComponent(leaseID) + '/revoke', {
        method: 'POST',
      });
      if (await followRelease(leaseID)) {
        showToast('success', 'Lease released.');
      } else {
        showToast('info', 'Not released yet: the computer holding this lease has not checked in. It lets go the next time it does.');
      }
    } catch (e) {
      const msg = (e && e.message) || 'unknown';
      showToast('error', 'Could not request release: ' + msg);
    } finally {
      state.releasingLeases.delete(leaseID);
      renderLeasesPage();
    }
  }

  // The holder drops the lease on its next sync, 10-15s after the click,
  // while the regular lease poll can be a minute away: poll until it's gone.
  async function followRelease(leaseID) {
    for (let i = 0; i < 20; i++) {
      await refreshLeaseData();
      if (!state.leases.some((l) => l.LeaseID === leaseID)) return true;
      await new Promise((r) => setTimeout(r, 3000));
    }
    return false;
  }

  // ----- keyboard shortcuts -----
  let helpReturnPage = null;
  document.addEventListener('keydown', (e) => {
    if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
    if (e.metaKey || e.ctrlKey) return;
    switch (e.key) {
      case 'r': fetchAll(); break;
      case '1': navigate('overview'); break;
      case '2': navigate('chats'); break;
      case '3': navigate('leases'); break;
      case '4': navigate('files'); break;
      case '5': navigate('events'); break;
      case '6': navigate('team'); break;
      case '7': navigate('analytics'); break;
      case '8': navigate('account'); break;
      case '9': navigate('settings'); break;
      case '?':
      case 'h':
      case 'H':
        // The same key closes Help, back to the page it was opened from.
        if (state.activePage === 'help') {
          navigate(helpReturnPage || 'overview');
        } else {
          helpReturnPage = state.activePage;
          navigate('help');
        }
        break;
      case 't': toggleTheme(); break;
      case 'Escape':
        $('drawer-backdrop').classList.remove('open');
        // Also dismiss confirm modal — simulate cancel click
        if ($('confirm-backdrop').classList.contains('open')) {
          $('confirm-cancel').click();
        }
        if ($('prompt-backdrop') && $('prompt-backdrop').classList.contains('open')) {
          $('prompt-cancel').click();
        }
        if ($('cancel-survey-backdrop') && $('cancel-survey-backdrop').classList.contains('open')) {
          $('cancel-survey-back').click();
        }
        if ($('billing-change-backdrop') && $('billing-change-backdrop').classList.contains('open')) {
          $('billing-change-cancel').click();
        }
        if ($('password-change-backdrop') && $('password-change-backdrop').classList.contains('open')) {
          $('password-cancel').click();
        }
        if ($('email-change-backdrop') && $('email-change-backdrop').classList.contains('open')) {
          $('email-cancel').click();
        }
        if ($('mfa-enroll-backdrop') && $('mfa-enroll-backdrop').classList.contains('open')) {
          $('mfa-cancel').click();
        }
        if ($('reauth-backdrop') && $('reauth-backdrop').classList.contains('open')) {
          $('reauth-cancel').click();
        }
        if ($('delete-blockers-backdrop') && $('delete-blockers-backdrop').classList.contains('open')) {
          $('delete-blockers-close').click();
        }
        if ($('delete-sent-backdrop') && $('delete-sent-backdrop').classList.contains('open')) {
          $('delete-sent-close').click();
        }
        // Recovery-codes modal intentionally does NOT close on Escape —
        // losing it before the user saves the codes is catastrophic. They
        // must click "I've saved them" (which requires Download or Copy first).
        break;
    }
  });

  // ----- theme -----
  function toggleTheme() {
    const cur = document.documentElement.getAttribute('data-theme') || 'dark';
    const next = cur === 'dark' ? 'light' : 'dark';
    document.documentElement.setAttribute('data-theme', next);
    try { localStorage.setItem('agentcollision-theme', next); } catch (e) {}
  }
  $('theme-toggle').addEventListener('click', toggleTheme);
  try {
    const saved = localStorage.getItem('agentcollision-theme');
    if (saved) document.documentElement.setAttribute('data-theme', saved);
  } catch (e) {}

  // ----- drawer close -----
  $('drawer-close').addEventListener('click', () => $('drawer-backdrop').classList.remove('open'));
  $('drawer-backdrop').addEventListener('click', (e) => {
    if (e.target === $('drawer-backdrop')) $('drawer-backdrop').classList.remove('open');
  });

  // ----- refresh button -----
  $('btn-refresh-all').addEventListener('click', () => {
    const icon = $('refresh-icon');
    icon.style.display = 'inline-block';
    icon.style.animation = 'spin 0.6s linear';
    setTimeout(() => { icon.style.animation = ''; }, 600);
    fetchAll();
  });


  // ----- team-server-unreachable banner: skip the backoff -----
  if ($('team-status-retry')) {
    $('team-status-retry').addEventListener('click', () => retryTeamNow(fetchAll));
  }

  // Data refreshes run 15-60s apart, so without this the page kept saying
  // "daemon online" for up to a minute after AgentCollision stopped (e.g.
  // restarting after an upgrade), and noticed its return just as late.
  if (isLocalDaemonMode() && typeof setInterval === 'function') {
    setInterval(async () => {
      if (document.visibilityState !== 'visible') return;
      try {
        await fetch('/v1/healthz', { cache: 'no-store' });
        if (isLocalDaemonDown()) retryTeamNow(fetchAll);
      } catch (e) {
        if (isLocalDaemonDown()) return;
        markTeamUnreachable(e, fetchAll, { localDown: true });
        renderAll();
      }
    }, 5000);
  }

  // Display Preferences panel removed 2026-05-12 — handlers for
  // settings-banner-reset / settings-decrypt-banner-reset deleted.
  // Banners auto-re-show when state requires; users who specifically
  // want a dismissed banner back can clear localStorage on the relevant
  // bannerDismissKey()/decryptBannerDismissKey() entry. Functionally
  // unused outside this panel.

  if ($('settings-unlink-team')) {
    $('settings-unlink-team').addEventListener('click', async () => {
      const ok = await showConfirm(
        'Unlink team?',
        'You will need to paste your team token again to see team activity. Your account and team data are not deleted.',
        'Unlink'
      );
      if (!ok) return;
      localStorage.removeItem('ac-team-token');
      localStorage.removeItem('ac-hmac-key');
      // The tenant binding goes with the team token — leaving it behind means
      // the next link sends a stale X-Team-ID until something overwrites it.
      localStorage.removeItem('ac-tenant-id');
      state.token = null;
      state.hmacKey = null;
      state.hmacKeyB64 = null;
      booted = false;
      // Trigger re-auth flow
      if (supabase) {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) { showLinkForm(user); return; }
      }
      showAuth();
    });
  }

  // signOutAfterPasswordChange consolidates the post-change cleanup
  // so the email-password module can drive it via one bridge call
  // instead of mutating booted / state.token / clearing cookies /
  // calling showAuth individually. Supabase invalidates other
  // sessions server-side on password change; we mirror that
  // client-side so the user is forced to re-authenticate.
  async function signOutAfterPasswordChange() {
    try {
      if (supabase) await supabase.auth.signOut();
    } catch {}
    booted = false;
    state.token = null;
    localStorage.removeItem("ac-team-token");
    localStorage.removeItem("ac-tenant-id");
    clearAuthHintCookie();
    showAuth();
  }


  // prefetchHandoffsBadge refreshes the badge with no user intent behind it.
  // It no-ops until boot() has run: before that point a first-run user can
  // still be in onboarding, with no user_teams row linked yet, and the request
  // would 401 against the coordinator. Marked background so that even an
  // unexpected 401 can't tear the session down (see api.js). Returns whether
  // it actually fired, so the initial scheduler below knows when to stop
  // re-arming.
  function prefetchHandoffsBadge() {
    if (!booted) return false;
    loadHandoffThreads({ background: true }).then(threads => {
      handoffsState.threads = threads;
      refreshHandoffsBadge();
    }).catch(() => {});
    return true;
  }
  // Background poll: every 30s when the page is in the foreground,
  // refresh the threads list so the badge stays current. Cheap —
  // one round trip — and the same cadence as teamsync, so the
  // dashboard never lies about cross-machine state for more than
  // ~30s.
  if (typeof setInterval === 'function') {
    setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      // Only fetch if we're not actively viewing the handoffs page;
      // when viewing, the page renderer handles its own refresh.
      if (state.activePage === 'handoffs') return;
      prefetchHandoffsBadge();
    }, 30 * 1000);
  }
  // Also kick off one fetch shortly after boot so the badge populates before
  // the user navigates to Handoffs.
  //
  // This used to be a bare 1500ms one-shot armed at page load. A returning
  // user is booted well inside that window, but a first-run user is still
  // clicking through onboarding — so the call fired before
  // createSoloWorkspace had run /v1/teams/link, 401'd, and took the
  // freshly-created team token down with it. Re-arm on the same cadence
  // until boot() has run, then fire once. The attempt cap stops a user who
  // abandons onboarding from polling forever; the 30s interval above picks
  // them up if they come back and finish.
  (function scheduleBadgePrefetch(attempt) {
    setTimeout(() => {
      if (prefetchHandoffsBadge()) return;
      if (attempt < 40) scheduleBadgePrefetch(attempt + 1);
    }, 1500);
  })(0);


  // ----- Supabase Auth -----
  // PR #7: Supabase only loads in cloud-pages and coordinator-hosted modes.
  // Local-daemon mode doesn't have a Supabase project (and shouldn't — the
  // daemon's own auth is loopback-token + JWKS, not Supabase). The
  // <script src="...supabase-js..."> CDN tag in index.html is gated on
  // AC_CONFIG.FEATURES.supabase, so window.supabase is undefined here in
  // daemon mode. We set `supabase` to null and short-circuit any caller
  // that tries to use it via the if (supabase) idiom that already exists
  // throughout the file.
  const SUPABASE_URL = 'https://sdkumimayzehxduacovy.supabase.co';
  const SUPABASE_ANON_KEY = 'sb_publishable_7JUVBRjWmeGu5yHPjmhiAw_dX8RmLy3';
  const __landedOnRecoveryLink = !!(location.hash && location.hash.includes('type=recovery'));
  let supabase = null;
  if (window.AC_CONFIG && window.AC_CONFIG.FEATURES.supabase && window.supabase) {
    try {
      supabase = window.supabase.createClient(SUPABASE_URL, SUPABASE_ANON_KEY);
    } catch (e) {
      console.error('Supabase client failed to initialize:', e);
      $('auth-screen').style.display = '';
    }
  }

  // loadHmacKeyAuto loads the team HMAC key (which decrypts file paths / branch
  // names) WITHOUT the user importing it by hand. Two paths by deploy mode:
  //
  //   - Cloud mode: ask the coordinator for the tenant's key via the
  //     authenticated /v1/teams/hmac-key endpoint — same auth as the rest of
  //     /v1/teams/*, no loopback / PNA / Origin-stripping fragility. The cloud
  //     already holds the key (invites table); "zero-knowledge" is operational,
  //     not cryptographic (see coordinator.ts:getTeamHmacKey).
  //
  //   - Local-daemon mode: fetch from the loopback daemon (daemon-discovery.js
  //     port-scan), with a self-healing retry loop for daemon-not-yet-started +
  //     PNA-mid-flight cases.
  //
  // Idempotent + best-effort: returns early if the key is already loaded, and a
  // failure just leaves the decrypt banner up (manual import still works). Called
  // unconditionally at boot so a flaky fetchAll never blocks it.
  // `skipFirstAttempt` (local-daemon mode only): the cold-boot path already did
  // one tryAutoImportHmacKey() before calling boot(), so skip the immediate
  // re-attempt here and go straight to the backoff retry loop — avoids a
  // redundant duplicate fetch on cold boot (the import is idempotent, but this
  // keeps the cadence clean).
  async function loadHmacKeyAuto(skipFirstAttempt = false) {
    if (state.hmacKey) { updateDecryptBanner(); return; }
    const cloudMode = window.AC_CONFIG && window.AC_CONFIG.DEPLOY_MODE !== 'local-daemon';
    if (cloudMode) {
      try {
        const resp = await api('/v1/teams/hmac-key');
        if (resp && resp.hmac_key_b64) {
          await loadHmacKey(resp.hmac_key_b64);
        }
      } catch (_) {
        // 404 here usually means the daemon hasn't registered the key with the
        // coordinator YET (it does so on its first sync cycle, ~5s after start)
        // — common for key-less tenants self-healing for the first time. Retry
        // on a short backoff so the key auto-loads once the daemon registers,
        // instead of leaving the user on the manual-import banner until they
        // reload. Stops the moment state.hmacKey is set. Auth errors (401) also
        // land here but simply find no key on each retry — harmless.
        const delays = [5_000, 15_000, 30_000, 60_000];
        let attempt = 0;
        const tryAgain = async () => {
          if (state.hmacKey) return;
          if (attempt >= 6) return;
          attempt++;
          try {
            const r = await api('/v1/teams/hmac-key');
            if (r && r.hmac_key_b64) {
              await loadHmacKey(r.hmac_key_b64);
              updateDecryptBanner();
              return;
            }
          } catch (_) { /* still not available — keep backing off */ }
          setTimeout(tryAgain, delays[Math.min(attempt - 1, delays.length - 1)]);
        };
        setTimeout(tryAgain, delays[0]);
      }
      updateDecryptBanner();
      return;
    }
    // Local-daemon path: try once (unless the caller already did), then retry on
    // backoff.
    const ok = skipFirstAttempt ? false : await tryAutoImportHmacKey();
    updateDecryptBanner();
    if (ok) return;
    // Cadence: 5s, 15s, 30s, 60s, then every 60s. Caps at 10 retries (~10 min).
    // Stops the moment state.hmacKey gets set.
    const delays = [5_000, 15_000, 30_000, 60_000];
    let attempt = 0;
    const tryAgain = async () => {
      if (state.hmacKey) return;
      if (attempt >= 10) return;
      attempt++;
      const got = await tryAutoImportHmacKey();
      updateDecryptBanner();
      if (got) return;
      const next = delays[Math.min(attempt - 1, delays.length - 1)];
      setTimeout(tryAgain, next);
    };
    setTimeout(tryAgain, delays[0]);
  }

  // ----- boot -----
  let booted = false;
  // `skipHmacFirstAttempt` is forwarded to loadHmacKeyAuto: the local-daemon
  // cold-boot path already tried one tryAutoImportHmacKey() before calling boot,
  // so it passes true to avoid an immediate duplicate fetch (all other callers
  // default false).
  function boot(skipHmacFirstAttempt = false) {
    if (booted) return; // prevent double-boot from onAuthStateChange + init
    booted = true;
    const initial = pageFromURL();
    if (initial.page !== 'overview') {
      state.activePage = '';
      navigate(initial.page, false, { threadID: initial.threadID });
      // If we resolved a legacy alias (e.g. /spend → protection),
      // rewrite the address bar to the canonical path so the stale
      // page-key doesn't linger. replaceState (not push) so Back
      // doesn't bounce the user to the retired URL. Account for the
      // daemon's /ui/ prefix: the SPA is served under /ui/, so the
      // canonical path mirrors whatever prefix we're already on.
      if (initial.aliased) {
        const prefix = location.pathname.startsWith('/ui/') ? '/ui/' : '/';
        history.replaceState({ page: initial.page, threadID: '' }, '', prefix + initial.page);
      }
    } else {
      // Cold-load on the Overview page: navigate() is skipped because
      // we're already there visually (the HTML ships with the overview
      // section pre-active). But state.activePage was initialized to
      // "home" — and several page renderers gate on
      // `state.activePage === "overview"` before responding to the
      // dashboard:tick event. Without this explicit sync, the
      // Overview's Recent Events panel never re-renders after fetchAll
      // populates state.events — it stays at "No events yet" until the
      // user manually navigates away and back.
      state.activePage = 'overview';
    }
    fetchAll().then(() => {
      // Now that auth has settled (fetchAll resolved, Supabase session
      // is restored, state.token is in place), it's safe to kick an
      // immediate worktrees-badge refresh. Earlier versions called
      // this synchronously at boot which raced auth setup — see PR
      // #222 for the postmortem. The scheduled tick already runs on
      // the leasesPoll cadence; this just removes the 15-60s cold-
      // boot lag so the badge populates within ~1 round trip of
      // first paint.
      refreshWorktreesBadge();
    }).catch(() => {
      // fetchAll can reject if any of its un-caught calls (/v1/teams/info,
      // /v1/teams/members, /v1/sync/state) hiccups — a transient 5xx, the
      // coordinator rate-limit, or sync/state being tier-gated. That must NOT
      // swallow the post-boot work below; the HMAC auto-load in particular runs
      // INDEPENDENTLY (see loadHmacKeyAuto, kicked off unconditionally next), so
      // a flaky boot fetch no longer forces a manual "Import from Local Daemon".
    });
    // HMAC key auto-load — fire it UNCONDITIONALLY, not chained on fetchAll's
    // success. It only needs the auth token (already settled before boot()), not
    // any fetchAll data. Previously this lived inside fetchAll().then(), so any
    // rejection in fetchAll (one flaky /v1/teams/* call) silently skipped it and
    // the user had to import the key by hand — the "I keep having to re-import"
    // bug. Decoupled here so the key loads on every authed boot.
    loadHmacKeyAuto(skipHmacFirstAttempt);
    // PR A scaling pass v2 (Free-tier gating arc, 2026-05-15): the
    // intervals here are now TIER-AWARE. Free users (DB tier='solo')
    // get 60s polls + no SSE; Team/Enterprise users get 15-30s
    // polls + live SSE that PR #134 shipped.
    //
    // scheduleDynamic wraps each task in a self-rescheduling
    // setTimeout that re-reads the tier's interval each cycle, so
    // mid-session upgrades (Stripe checkout → webhook → teamInfo
    // refresh) flip cadence on the next tick without a page reload.
    scheduleDynamic("leasesPoll", refreshLeases);
    // Worktrees count badge: previously only refreshed when the user
    // clicked into the Worktrees page. Now ticks on the same cadence
    // as leasesPoll (Free 60s, Team 15s) so the sidebar number is
    // current without a click. Cheap — limit=1 in cloud mode so we
    // pay for the active_count header only, not the row decryption.
    //
    // Don't kick an immediate call here: a synchronous fire on boot
    // races Supabase session restoration. With no bearer, api()
    // would call showAuth() and trip the "Coordinator unreachable"
    // headline for already-signed-in users on every page load. Let
    // the scheduled tick fire after the first leasesPoll interval
    // (15-60s by tier) by which point fetchAll() + Supabase auth
    // have both settled. The page render also fetches the badge
    // value when the user navigates into Worktrees, so the cold-
    // boot window is the only place the badge lags.
    scheduleDynamic("leasesPoll", refreshWorktreesBadge);
    // Phase 1 (v2): decision-pill counts. Refresh on boot then on
    // the tier's countsPoll cadence. Cheap server-side (in-process
    // histogram); SSE-on tiers also have the live stream, so this
    // is purely a tile-aggregate refresh that can be eventually-
    // consistent.
    refreshDecisionCounts();
    scheduleDynamic("countsPoll", refreshDecisionCounts);

    // Audit-trail refresh. Audit events (team rename, role CRUD,
    // member add/remove) are admin-action rows in audit_log, not
    // coordination_events — they don't flow through SSE OR the
    // refreshLeaseData events poll. This is the only path that
    // keeps audit-row state fresh during a session.
    // Free tier auditPoll: 10min cadence (vs Team's 5min) — audit
    // volume on a solo tenant is near-zero anyway.
    scheduleDynamic("auditPoll", refreshAudit);
    // Pre-Phase-J this had a 10s setInterval that explicitly
    // re-rendered each active page. dashboard:tick (dispatched from
    // renderAll() on every ~5s poll) now covers the same need via
    // each page module's tick subscriber — no separate interval.
    // Populate team switcher if user has Supabase session
    renderTeamSwitcher();
    // BU switcher sits below the team switcher — wires its toggle
    // button + outside-click handler once, then renders the menu.
    // No-op (hides itself) if the tenant has no BUs or only the
    // default. Per Phase 6.1 of the v4 multi-team daemon arc.
    try {
      wireBuSwitcher();
      renderBuSwitcher();
    } catch (e) {
      console.warn('[boot] bu-switcher init failed:', e);
    }
    // If user arrived via a pricing CTA with ?tier=xxx, jump them to checkout
    if (typeof window._applyTierIntent === 'function') {
      setTimeout(() => window._applyTierIntent(), 400);
    }
  }

  // ----- Team switcher -----
  async function renderTeamSwitcher() {
    const wrap = $('team-switcher-wrap');
    if (!wrap || !supabase) return;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return; // Team switcher only for Supabase-authenticated users

      const r = await fetch(COORDINATOR + '/v1/user/teams', {
        headers: { 'Authorization': 'Bearer ' + session.access_token },
      });
      if (!r.ok) return;
      const data = await r.json();
      const teams = data.teams || [];

      if (teams.length === 0) return;

      // Always show switcher once user has at least 1 team (helps them see which team they're on)
      wrap.style.display = '';

      // Find current team via team info endpoint — match team_id
      const currentInfo = await api('/v1/teams/info').catch(() => null);
      const currentTeamId = currentInfo?.team_id || teams[0].team_id;
      const currentTeam = teams.find(t => t.team_id === currentTeamId) || teams[0];
      window._currentTeamId = currentTeamId;

      $('team-switcher-current').textContent = currentTeam.team_name;

      // Build menu
      const menu = $('team-switcher-menu');
      menu.innerHTML = teams.map(t => {
        const isActive = t.team_id === currentTeamId;
        return '<button data-team-switch="' + escapeHTML(t.team_id) + '" style="width: 100%; display: block; padding: 10px 12px; background: ' + (isActive ? 'var(--accent-bg-strong)' : 'none') + '; border: none; border-bottom: 1px solid var(--border); color: ' + (isActive ? 'var(--accent)' : 'var(--text)') + '; font-family: inherit; font-size: 13px; text-align: left; cursor: pointer;">' +
          '<div style="font-weight: 600;">' + escapeHTML(t.team_name) + (isActive ? ' <span style="font-size: 10px; color: var(--accent); margin-left: 4px;">&#10003; current</span>' : '') + '</div>' +
          '<div style="font-size: 11px; color: var(--text-tertiary); margin-top: 2px; text-transform: capitalize;">' + escapeHTML(t.role) + '</div>' +
        '</button>';
      }).join('');
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[team-switcher] render failed:', e);
    }
  }

  // Toggle menu + handle switches
  document.addEventListener('click', (e) => {
    if (e.target.closest('#team-switcher-btn')) {
      const menu = $('team-switcher-menu');
      if (menu) menu.style.display = menu.style.display === 'none' ? '' : 'none';
      return;
    }
    const switchBtn = e.target.closest('[data-team-switch]');
    if (switchBtn) {
      const teamId = switchBtn.dataset.teamSwitch;
      $('team-switcher-menu').style.display = 'none';
      // Clicking the current team = no-op. Only show the switch dialog for a different team.
      if (!teamId || teamId === window._currentTeamId) return;
      showConfirm(
        'Switch teams?',
        'To switch teams, sign out and link the other team\u2019s token.<br>Multi-team in-place switching is coming soon.',
        'OK'
      );
      return;
    }
    // Click outside → close menu
    const menu = $('team-switcher-menu');
    if (menu && menu.style.display !== 'none' && !e.target.closest('#team-switcher-wrap')) {
      menu.style.display = 'none';
    }
  });

  function hideLoading() {
    const el = $('loading-screen');
    if (el) el.style.display = 'none';
  }

  function showAuth() {
    hideLoading();
    // Drop the cached loopback token on every sign-out so the next
    // session has to re-fetch it from the daemon, leaving an audit
    // log entry under the new user.
    try { sessionStorage.removeItem('ac-loopback-token'); } catch {}
    // Tear down the coordinator-reconnect banner + its probe timer. showAuth()
    // is the single chokepoint every logout / auth-screen path funnels through,
    // so clearing here guarantees the fixed-position banner (which lives outside
    // .app, so hiding .app wouldn't remove it) can't stay painted over the login
    // screen, and the 8s probe can't keep firing showAuth() after sign-out.
    if (window._coordinatorReconnectTimer) {
      clearInterval(window._coordinatorReconnectTimer);
      window._coordinatorReconnectTimer = null;
    }
    const _reconnectBar = document.getElementById('coordinator-reconnect-bar');
    if (_reconnectBar) _reconnectBar.style.display = 'none';
    $('auth-screen').style.display = '';
    document.querySelector('.app').style.display = 'none';
    // If user arrived via ?signup=1 (marketing/docs CTAs), default to signup.
    // Otherwise show login.
    const params = new URLSearchParams(location.search);
    const wantsSignup = params.get('signup') === '1';
    const tierIntent = params.get('tier'); // 'startup' or 'enterprise' when from a trial CTA
    $('auth-login-form').style.display = wantsSignup ? 'none' : '';
    $('auth-signup-form').style.display = wantsSignup ? '' : 'none';
    $('auth-link-form').style.display = 'none';
    $('auth-forgot-form').style.display = 'none';
    $('auth-reset-form').style.display = 'none';
    $('auth-check-email').style.display = 'none';

    const heading = $('auth-heading');
    const sub = $('auth-subheading');
    const badge = $('auth-trial-badge');
    if (wantsSignup) {
      // Free tier (default) vs. paid-tier trial — different framing
      if (tierIntent === 'startup' || tierIntent === 'enterprise') {
        if (heading) heading.textContent = 'Start your 14-day trial';
        if (sub) sub.innerHTML = 'Create your AgentCollision account. No credit card required.';
        if (badge) { badge.textContent = '14-day free trial'; badge.style.display = ''; }
      } else {
        if (heading) heading.textContent = 'Create your account';
        if (sub) sub.innerHTML = 'Free forever for solo developers. Paid plans unlock team coordination across machines.';
        if (badge) badge.style.display = 'none';
      }
    } else {
      if (heading) heading.textContent = 'Welcome back';
      if (sub) sub.innerHTML = 'Sign in to your AgentCollision dashboard.';
      if (badge) badge.style.display = 'none';
    }
  }

  // EARLY auth-bridge seed. The full window.AC_LEGACY object is assembled
  // ~1600 lines below, but boot()/handleAuthSuccess fire authed fetches
  // BEFORE that point — and a 401 there calls api.js's showAuth() via
  // window.AC_LEGACY.showAuth. Without this seed that bridge is undefined
  // during early boot, so a 401 left the user stuck on a dead screen with
  // only a console error ("showAuth bridge missing"). Seed it the moment
  // showAuth is defined (hoisting guarantees the reference); the big
  // assignment below merges the rest. Fixes the boot-order regression.
  window.AC_LEGACY = window.AC_LEGACY || {};
  window.AC_LEGACY.showAuth = showAuth;

  function showDashboard() {
    hideLoading();
    $('auth-screen').style.display = 'none';
    document.querySelector('.app').style.display = '';
    // Land at the TOP of the page when the dashboard first becomes visible.
    // The auth/loading screens (and Safari's scroll restoration after a
    // homescreen-app relaunch) can leave the window scrolled partway down, so
    // a freshly logged-in user opened Overview mid-page. Reset window scroll
    // and the content scroll container. rAF so it runs after the layout the
    // newly-shown .app produces (a synchronous scrollTo can be clobbered by
    // the browser's own post-paint scroll restoration).
    try {
      if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
    } catch (_) { /* non-fatal */ }
    const toTop = () => {
      window.scrollTo(0, 0);
      const content = document.querySelector('.content');
      if (content) content.scrollTop = 0;
    };
    toTop();
    requestAnimationFrame(toTop);
    // Cross-subdomain auth hint for the marketing site (agentcollision.com),
    // written HERE — at the single authenticated "dashboard is visible" point —
    // not only from billing.js. The billing path is gated behind a successful
    // /v1/billing/status fetch and only runs on the billing page, so a user who
    // logs in and lands on Overview (or whose coordinator is rate-limited /
    // down) never got the cookie and the marketing nav kept showing "Sign In"
    // despite a live session. Tier defaults to the cached billing tier when
    // known, else "solo"; billing.js refines it with the authoritative tier on
    // its next load. Best-effort + no-op on localhost (see writeAuthHintCookie).
    try {
      writeAuthHintCookie((window._billing && window._billing.tier) || "solo");
    } catch (_) { /* advisory UX cookie — never block the dashboard */ }
  }
  function showLinkForm(user) {
    hideLoading();
    $('auth-screen').style.display = '';
    $('auth-login-form').style.display = 'none';
    $('auth-signup-form').style.display = 'none';
    $('auth-link-form').style.display = '';
    $('auth-error').style.display = 'none';
    $('auth-success').style.display = 'none';

    // Reframe the outer auth screen — this is first-run setup, not a team
    // dashboard login. Hide the "Team Dashboard" heading that assumes teams.
    const heading = $('auth-heading');
    const sub = $('auth-subheading');
    const badge = $('auth-trial-badge');
    if (heading) heading.textContent = 'Welcome to AgentCollision';
    if (sub) sub.textContent = 'Let\u2019s get you set up.';
    if (badge) badge.style.display = 'none';

    const greeting = $('auth-link-greeting');
    if (user && user.email) {
      greeting.textContent = 'Signed in as ' + user.email;
    } else {
      greeting.textContent = '';
    }
    // Default to the choice step
    showOnboardStep('choose');
  }
  // True once the user enters the password-recovery flow (either via recovery
  // hash on load or a PASSWORD_RECOVERY auth event). Stays true until the
  // user successfully updates their password. Prevents SIGNED_IN/SIGNED_OUT
  // side-effects from clobbering the reset form while the user is typing.
  let recoveryInProgress = false;
  function showResetForm() {
    recoveryInProgress = true;
    showAuth();
    $('auth-login-form').style.display = 'none';
    $('auth-signup-form').style.display = 'none';
    $('auth-forgot-form').style.display = 'none';
    $('auth-check-email').style.display = 'none';
    $('auth-reset-form').style.display = '';
    setAuthHeading('reset');
  }
  function showError(msg) {
    $('auth-error').textContent = msg;
    $('auth-error').style.display = '';
    $('auth-success').style.display = 'none';
  }
  function showSuccess(msg) {
    $('auth-success').textContent = msg;
    $('auth-success').style.display = '';
    $('auth-error').style.display = 'none';
  }

  // Sign out
  $('btn-signout').addEventListener('click', async () => {
    const ok = await showConfirm(
      'Sign out?',
      'You will need to sign in again to access the dashboard. Your team link will be preserved.',
      'Sign Out'
    );
    if (!ok) return;
    await supabase.auth.signOut();
    booted = false;
    state.token = null;
    localStorage.removeItem('ac-team-token');
    // Drop the tenant binding too: the next sign-in may be a different
    // account, and a stale X-Team-ID resolves to a tenant they aren't a
    // member of. The coordinator ignores it in that case, so the symptom is
    // a silently wrong tenant rather than an error.
    localStorage.removeItem('ac-tenant-id');
    // Clear the cross-subdomain auth hint so the marketing site
    // (agentcollision.com) flips back to "Sign In" — the inverse of the
    // write in showDashboard(). Without this, a signed-out user keeps
    // seeing "Open Dashboard" on the marketing nav for the cookie's
    // 7-day Max-Age.
    clearAuthHintCookie();
    showAuth();
  });

  // Sign out from link form
  $('auth-link-signout').addEventListener('click', async (e) => {
    e.preventDefault();
    if (supabase) await supabase.auth.signOut();
    booted = false;
    state.token = null;
    localStorage.removeItem('ac-team-token');
    localStorage.removeItem('ac-tenant-id');
    clearAuthHintCookie();
    showAuth();
  });

  // Update the auth card's heading/subheading to match whichever form is showing.
  // The header lives outside each form (shared), so it doesn't update by itself
  // when we swap panels. Keep strings in one place so future wording changes
  // don't drift.
  function setAuthHeading(mode) {
    const h = $('auth-heading');
    const s = $('auth-subheading');
    const b = $('auth-trial-badge');
    if (b) b.style.display = 'none';
    if (mode === 'signup') {
      if (h) h.textContent = 'Create your account';
      if (s) s.innerHTML = 'Free forever for solo developers. Paid plans unlock team coordination across machines.';
    } else if (mode === 'forgot') {
      if (h) h.textContent = 'Reset your password';
      if (s) s.textContent = 'We\u2019ll email you a link to set a new one.';
    } else if (mode === 'reset') {
      if (h) h.textContent = 'Choose a new password';
      if (s) s.textContent = 'Enter the password you\u2019d like to use from now on.';
    } else {
      if (h) h.textContent = 'Welcome back';
      if (s) s.innerHTML = 'Sign in to your AgentCollision dashboard.';
    }
  }

  // Toggle login/signup. Also hides the "check your email" panel — the user
  // shouldn't see a dangling "we sent you an email" if they navigate away.
  $('auth-show-signup').addEventListener('click', (e) => {
    e.preventDefault();
    $('auth-login-form').style.display = 'none';
    $('auth-signup-form').style.display = '';
    $('auth-check-email').style.display = 'none';
    $('auth-error').style.display = 'none';
    $('auth-success').style.display = 'none';
    setAuthHeading('signup');
  });
  $('auth-show-login').addEventListener('click', (e) => {
    e.preventDefault();
    $('auth-login-form').style.display = '';
    $('auth-signup-form').style.display = 'none';
    $('auth-check-email').style.display = 'none';
    $('auth-forgot-form').style.display = 'none';
    $('auth-error').style.display = 'none';
    $('auth-success').style.display = 'none';
    setAuthHeading('login');
  });

  // Forgot password toggle.
  $('auth-show-forgot').addEventListener('click', (e) => {
    e.preventDefault();
    $('auth-login-form').style.display = 'none';
    $('auth-signup-form').style.display = 'none';
    $('auth-check-email').style.display = 'none';
    $('auth-forgot-form').style.display = '';
    $('auth-error').style.display = 'none';
    $('auth-success').style.display = 'none';
    setAuthHeading('forgot');
    // Prefill the forgot form with whatever's in the login email field — saves a retype.
    const loginEmail = $('auth-email').value.trim();
    if (loginEmail) $('forgot-email').value = loginEmail;
  });
  $('auth-show-login-from-forgot').addEventListener('click', (e) => {
    e.preventDefault();
    $('auth-login-form').style.display = '';
    $('auth-forgot-form').style.display = 'none';
    $('auth-error').style.display = 'none';
    $('auth-success').style.display = 'none';
    setAuthHeading('login');
  });

  // Login
  $('auth-login-btn').addEventListener('click', async () => {
    const email = $('auth-email').value.trim();
    const password = $('auth-password').value;
    if (!email || !password) { showError('Email and password required.'); return; }
    $('auth-login-btn').disabled = true;
    $('auth-login-btn').textContent = 'Signing in...';
    $('auth-error').style.display = 'none';
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) {
        // Clear the password so the user isn't staring at dots they need to retype or re-edit.
        // Email stays — wrong password is the overwhelmingly common case, not wrong email.
        $('auth-password').value = '';
        // "Email not confirmed" is the one login error the user can fix
        // themselves, but on its own it's a dead end — it names the problem
        // and offers no way out, so these users file a ticket instead.
        // Password recovery IS the fix: clicking the emailed recovery link
        // sets email_confirmed_at, so it activates the account AND sets a
        // password in one pass. Open that form for them with the address
        // prefilled rather than making them find "Forgot password?".
        if (error.code === 'email_not_confirmed' || error.message === 'Email not confirmed') {
          $('auth-login-form').style.display = 'none';
          $('auth-signup-form').style.display = 'none';
          $('auth-check-email').style.display = 'none';
          $('auth-forgot-form').style.display = '';
          const forgotEmail = $('forgot-email');
          if (forgotEmail) forgotEmail.value = email;
          setAuthHeading('forgot');
          $('auth-success').style.display = 'none';
          showError('Your email isn’t confirmed yet. Send yourself a link below to activate your account and set a password.');
          track('auth.login_unconfirmed');
          return;
        }
        showError(error.message);
        track('auth.login_failed', { reason: error.message });
        return;
      }
      track('auth.login');
      identifyUser(data.user);
      await handleAuthSuccess(data.user);
    } finally {
      $('auth-login-btn').disabled = false;
      $('auth-login-btn').textContent = 'Sign In';
    }
  });
  $('auth-password').addEventListener('keydown', (e) => { if (e.key === 'Enter') $('auth-login-btn').click(); });

  // Signup
  $('auth-signup-btn').addEventListener('click', async () => {
    const email = $('signup-email').value.trim();
    const password = $('signup-password').value;
    if (!email || !password) { showError('Email and password required.'); return; }
    if (password.length < 6) { showError('Password must be at least 6 characters.'); return; }
    $('auth-signup-btn').disabled = true;
    $('auth-signup-btn').textContent = 'Creating account...';
    $('auth-error').style.display = 'none';
    try {
      const { data, error } = await supabase.auth.signUp({ email, password });
      if (error) { showError(error.message); track('auth.signup_failed', { reason: error.message }); return; }

      // Supabase returns the same shape for (a) brand-new signup pending
      // confirmation AND (b) already-registered user. The distinguisher is
      // data.user.identities — empty array means "user already exists, we
      // silently de-duped." In that case the confirmation email WON'T fire
      // (nothing to confirm). Tell the user to sign in instead.
      const identitiesEmpty = !data.user?.identities || data.user.identities.length === 0;
      if (data.user && !data.user.email_confirmed_at && identitiesEmpty) {
        showError('An account with this email already exists. Try signing in, or use "Forgot password" to reset it.');
        track('auth.signup_duplicate');
        return;
      }

      track('auth.signup');
      identifyUser(data.user);

      // Marketing opt-in (separate from the transactional welcome/account
      // email). Only enroll if the user explicitly ticked the consent box.
      // Fire-and-forget; the worker also gates on consent === true server-side.
      try {
        const consent = !!$('signup-marketing-consent')?.checked;
        if (consent) {
          fetch('https://subscribe.agentcollision.com/v1/marketing/ingest', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              email,
              consent: true,
              source: 'signup',
              supabaseUserId: data.user?.id || null,
              consentSource: 'signup-checkbox',
            }),
          }).catch(() => {});
        }
      } catch (_) { /* never block signup on marketing enroll */ }

      if (data.user && !data.user.email_confirmed_at) {
        // Swap the signup form for the dedicated "check your email" panel.
        // Clearing the password field avoids leaving the user staring at the
        // same form they just submitted (which previously looked like nothing
        // happened).
        $('signup-password').value = '';
        $('auth-signup-form').style.display = 'none';
        $('auth-check-email-address').textContent = email;
        $('auth-check-email').style.display = '';
        $('auth-error').style.display = 'none';
        $('auth-success').style.display = 'none';
        // Remember the email so "Resend" and "Start over" work without
        // pulling it back out of the DOM.
        window._pendingSignupEmail = email;
        return;
      }
      await handleAuthSuccess(data.user);
    } finally {
      $('auth-signup-btn').disabled = false;
      $('auth-signup-btn').textContent = 'Create Account';
    }
  });
  $('signup-password').addEventListener('keydown', (e) => { if (e.key === 'Enter') $('auth-signup-btn').click(); });

  // GitHub OAuth (sign-in + sign-up share one handler — Supabase
  // upserts the user either way on first successful OAuth callback).
  // On click we redirect away from the dashboard to Supabase, which
  // hands off to github.com/login/oauth/authorize, then back to
  // Supabase's /auth/v1/callback, then back to redirectTo with a
  // session in the URL hash. The existing onAuthStateChange
  // listener picks up the SIGNED_IN event and runs handleAuthSuccess.
  async function startGitHubOAuth(buttonId) {
    const btn = $(buttonId);
    if (btn) { btn.disabled = true; btn.style.opacity = '0.7'; }
    $('auth-error').style.display = 'none';
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'github',
        options: {
          // redirectTo must match one of the Redirect URLs configured
          // in Supabase Auth → URL Configuration. window.location.origin
          // resolves to https://dashboard.agentcollision.com in prod
          // and http://localhost:<port> in dev — both should be in
          // the allow-list there.
          redirectTo: window.location.origin,
        },
      });
      if (error) {
        showError('GitHub sign-in failed: ' + error.message);
        track('auth.github_failed', { reason: error.message });
        if (btn) { btn.disabled = false; btn.style.opacity = ''; }
      }
      // No-op on success — the browser navigates away.
    } catch (e) {
      showError('GitHub sign-in failed: ' + (e.message || 'unknown'));
      if (btn) { btn.disabled = false; btn.style.opacity = ''; }
    }
  }
  if ($('auth-login-github')) $('auth-login-github').addEventListener('click', () => startGitHubOAuth('auth-login-github'));
  if ($('auth-signup-github')) $('auth-signup-github').addEventListener('click', () => startGitHubOAuth('auth-signup-github'));

  // Forgot password — send reset email.
  // Supabase sends a magic link with a recovery token in the URL hash.
  // When the user clicks it and lands back here, onAuthStateChange fires
  // a PASSWORD_RECOVERY event (handled below) which shows the reset form.
  $('auth-forgot-btn').addEventListener('click', async () => {
    const email = $('forgot-email').value.trim();
    if (!email) { showError('Email required.'); return; }
    $('auth-forgot-btn').disabled = true;
    $('auth-forgot-btn').textContent = 'Sending...';
    $('auth-error').style.display = 'none';
    try {
      // Redirect back to this exact page so the recovery token lands in the URL
      // and onAuthStateChange can pick it up.
      const redirectTo = window.location.origin + window.location.pathname;
      const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo });
      if (error) { showError(error.message); track('auth.forgot_failed', { reason: error.message }); return; }
      track('auth.forgot_sent');
      // Don't reveal whether the email exists — always show the same success
      // message. Supabase also returns no error for unknown emails, but we
      // keep the copy neutral anyway.
      showSuccess('If an account exists for that email, a reset link is on its way.');
      $('forgot-email').value = '';
    } finally {
      $('auth-forgot-btn').disabled = false;
      $('auth-forgot-btn').textContent = 'Send Reset Link';
    }
  });
  $('forgot-email').addEventListener('keydown', (e) => { if (e.key === 'Enter') $('auth-forgot-btn').click(); });

  // Reset password — user has clicked the recovery link and is now signed in
  // with a recovery session. updateUser() sets the new password.
  $('auth-reset-btn').addEventListener('click', async () => {
    const password = $('reset-password').value;
    if (!password) { showError('Password required.'); return; }
    if (password.length < 6) { showError('Password must be at least 6 characters.'); return; }
    $('auth-reset-btn').disabled = true;
    $('auth-reset-btn').textContent = 'Updating...';
    $('auth-error').style.display = 'none';
    try {
      const { data, error } = await supabase.auth.updateUser({ password });
      if (error) { showError(error.message); track('auth.reset_failed', { reason: error.message }); return; }
      track('auth.reset_success');
      recoveryInProgress = false;
      $('reset-password').value = '';
      $('auth-reset-form').style.display = 'none';
      showSuccess('Password updated. Signing you in…');
      // Clean the recovery token out of the URL so a refresh doesn't re-trigger the flow.
      if (window.location.hash) history.replaceState(null, '', window.location.pathname);
      if (data?.user) {
        identifyUser(data.user);
        await handleAuthSuccess(data.user);
      }
    } finally {
      $('auth-reset-btn').disabled = false;
      $('auth-reset-btn').textContent = 'Update Password';
    }
  });
  $('reset-password').addEventListener('keydown', (e) => { if (e.key === 'Enter') $('auth-reset-btn').click(); });

  // "Check your email" panel buttons.
  $('auth-check-email-resend').addEventListener('click', async () => {
    const email = window._pendingSignupEmail;
    const statusEl = $('auth-check-email-status');
    if (!email) {
      statusEl.textContent = 'No email on file. Click "Start over" and try again.';
      statusEl.style.color = 'var(--danger)';
      return;
    }
    const btn = $('auth-check-email-resend');
    btn.disabled = true;
    btn.textContent = 'Sending...';
    statusEl.textContent = '';
    try {
      // Supabase resend() re-sends the signup confirmation email for
      // a user who's in the "waiting to confirm" state. Rate-limited
      // server-side so spamming the button is harmless.
      const { error } = await supabase.auth.resend({ type: 'signup', email });
      if (error) {
        statusEl.textContent = error.message || 'Could not resend. Wait a minute and try again.';
        statusEl.style.color = 'var(--danger)';
      } else {
        statusEl.textContent = 'Confirmation email sent again. Check your inbox.';
        statusEl.style.color = 'var(--success)';
      }
    } catch (e) {
      statusEl.textContent = (e && e.message) || 'Network error';
      statusEl.style.color = 'var(--danger)';
    } finally {
      btn.disabled = false;
      btn.textContent = 'Resend confirmation email';
    }
  });

  $('auth-check-email-back').addEventListener('click', (e) => {
    e.preventDefault();
    $('auth-check-email').style.display = 'none';
    $('auth-signup-form').style.display = '';
    $('signup-email').value = '';
    $('signup-password').value = '';
    $('auth-error').style.display = 'none';
    $('auth-success').style.display = 'none';
    $('auth-check-email-status').textContent = '';
    window._pendingSignupEmail = null;
  });

  // ============================================================================
  // AAL2 enforcement — blocks dashboard entry after sign-in if the user has a
  // verified TOTP factor but hasn't proven it this session. Shows an in-page
  // full-screen challenge. 5 wrong codes → hard sign-out (defense-in-depth;
  // Supabase also rate-limits server-side). Recovery code path is supported.
  //
  // Returns true when the user has reached AAL2 (either via TOTP or a valid
  // recovery code), false when they cancelled or got locked out. On false
  // we've already signed them out + returned them to the auth screen.
  // ============================================================================
  async function enforceAAL2IfRequired() {
    if (!supabase) return true; // no supabase → nothing to enforce
    // Gate on an ACTUAL verified TOTP factor first. This is the source of
    // truth for "does this user use MFA." Supabase's
    // getAuthenticatorAssuranceLevel() can transiently report
    // nextLevel:'aal2' during session rehydration on a hard reload — before
    // the token fully settles — even for a user with NO factors. The old
    // code trusted nextLevel alone, so that transient reading sent
    // factor-less users into promptTOTPChallenge() (which shows the auth
    // screen and waits for a code they don't have) — indistinguishable from
    // being logged out. Checking listFactors() first means a user with no
    // verified factor is NEVER challenged, regardless of a flaky AAL read.
    let factors;
    try {
      factors = (await supabase.auth.mfa.listFactors()).data;
    } catch (_) {
      // listFactors itself failed (network hiccup on boot). Fail OPEN — a
      // transient API error is not a security event, and a user with no MFA
      // must not be locked out of their own dashboard. The server still
      // enforces real authz on every request; the worst case of failing open
      // here is that a genuine aal2 user briefly isn't re-challenged client-
      // side on one boot, which the next navigation corrects.
      return true;
    }
    const hasVerified =
      factors && (factors.totp || []).some((f) => f.status === 'verified');
    if (!hasVerified) return true; // no MFA configured → never challenge

    // The user DOES have a verified factor — now consult AAL to see if this
    // session still needs to step up to aal2.
    let aal;
    try {
      const r = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      aal = r && r.data;
    } catch (_) {
      aal = null;
    }
    // Could not read AAL but we KNOW they have a factor → fail closed: prompt.
    if (!aal) return await promptTOTPChallenge();
    if (aal.nextLevel !== 'aal2' || aal.currentLevel === 'aal2') return true;
    return await promptTOTPChallenge();
  }

  // Shows the #mfa-challenge-screen, wires the Verify/Recovery/Back controls,
  // returns a promise that resolves true on successful challenge, false on
  // cancel or lockout.
  function promptTOTPChallenge() {
    return new Promise(async (resolve) => {
      const screen = $('mfa-challenge-screen');
      const subtitle = $('mfa-challenge-subtitle');
      const input = $('mfa-challenge-input');
      const errBox = $('mfa-challenge-error');
      const verifyBtn = $('mfa-challenge-verify');
      const recoveryLink = $('mfa-challenge-use-recovery');
      const backLink = $('mfa-challenge-back');
      const loginForm = $('auth-login-form');
      const signupForm = $('auth-signup-form');
      const forgotForm = $('auth-forgot-form');

      // Hide the other auth forms so only the challenge is visible.
      if (loginForm) loginForm.style.display = 'none';
      if (signupForm) signupForm.style.display = 'none';
      if (forgotForm) forgotForm.style.display = 'none';
      screen.style.display = '';

      // Ensure the auth screen container is visible.
      const authScreen = $('auth-screen');
      if (authScreen) authScreen.style.display = '';

      let mode = 'totp'; // 'totp' or 'recovery'
      let factorId = null;
      let challengeId = null;
      let wrongCount = 0;
      const MAX_WRONG = 5;

      // Look up the verified TOTP factor.
      try {
        const lf = await supabase.auth.mfa.listFactors();
        const totp = (lf.data && lf.data.totp) || [];
        const verified = totp.find((f) => f.status === 'verified');
        if (!verified) {
          // Shouldn't happen — getAuthenticatorAssuranceLevel said aal2 was next.
          // Fail closed: sign out.
          await supabase.auth.signOut();
          cleanup();
          resolve(false);
          return;
        }
        factorId = verified.id;
        const ch = await supabase.auth.mfa.challenge({ factorId });
        if (ch.error) throw ch.error;
        challengeId = ch.data.id;
      } catch (e) {
        errBox.textContent = 'Could not start challenge: ' + ((e && e.message) || 'unknown');
        try { await supabase.auth.signOut(); } catch (_) {}
      }

      input.value = '';
      input.focus();

      function cleanup() {
        screen.style.display = 'none';
        verifyBtn.disabled = false;
        verifyBtn.textContent = 'Verify';
        input.value = '';
        errBox.textContent = '';
        // Restore login form visibility on the way out — the caller may or may
        // not show it depending on resolve(true/false).
        if (loginForm) loginForm.style.display = '';
        // Detach listeners so repeated challenges don't stack handlers.
        verifyBtn.replaceWith(verifyBtn.cloneNode(true));
        recoveryLink.replaceWith(recoveryLink.cloneNode(true));
        backLink.replaceWith(backLink.cloneNode(true));
        input.replaceWith(input.cloneNode(true));
      }

      // Lookup the fresh nodes after cloneNode-based cleanup of previous runs.
      const verifyBtnLive = $('mfa-challenge-verify');
      const recoveryLinkLive = $('mfa-challenge-use-recovery');
      const backLinkLive = $('mfa-challenge-back');
      const inputLive = $('mfa-challenge-input');

      async function handleVerify() {
        errBox.textContent = '';
        const code = (inputLive.value || '').trim().toUpperCase();
        if (mode === 'totp') {
          if (!/^\d{6}$/.test(code)) { errBox.textContent = 'Enter the 6-digit code from your authenticator app.'; return; }
          verifyBtnLive.disabled = true; verifyBtnLive.textContent = 'Verifying\u2026';
          try {
            const { error } = await supabase.auth.mfa.verify({ factorId, challengeId, code });
            if (error) throw error;
            cleanup();
            resolve(true);
          } catch (e) {
            wrongCount++;
            if (wrongCount >= MAX_WRONG) {
              errBox.textContent = 'Too many incorrect attempts. Signing out.';
              try { await supabase.auth.signOut(); } catch (_) {}
              cleanup();
              resolve(false);
              return;
            }
            errBox.textContent = (e && e.message ? e.message : 'Code didn\u2019t verify.') + ' ' + (MAX_WRONG - wrongCount) + ' tries remaining.';
            // Re-create a fresh challenge so the user can retry without
            // starting over.
            try {
              const ch = await supabase.auth.mfa.challenge({ factorId });
              if (!ch.error) challengeId = ch.data.id;
            } catch (_) {}
            inputLive.value = '';
            inputLive.focus();
            verifyBtnLive.disabled = false;
            verifyBtnLive.textContent = 'Verify';
          }
        } else {
          if (!/^[A-Z0-9]{4}-[A-Z0-9]{4}$/.test(code)) { errBox.textContent = 'Recovery code must be formatted XXXX-XXXX.'; return; }
          verifyBtnLive.disabled = true; verifyBtnLive.textContent = 'Verifying\u2026';
          try {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) throw new Error('Session expired. Sign in again.');
            const r = await fetch(COORDINATOR + '/v1/user/mfa/verify-recovery', {
              method: 'POST',
              headers: { 'Authorization': 'Bearer ' + session.access_token, 'Content-Type': 'application/json' },
              body: JSON.stringify({ code }),
            });
            if (!r.ok) {
              const er = await r.json().catch(() => ({}));
              throw new Error(er.message || er.error || 'Code didn\u2019t verify.');
            }
            // Supabase's own aal is still aal1 — but the server has confirmed
            // our recovery code. This is our AAL2 gate; pass.
            cleanup();
            resolve(true);
          } catch (e) {
            wrongCount++;
            if (wrongCount >= MAX_WRONG) {
              errBox.textContent = 'Too many incorrect attempts. Signing out.';
              try { await supabase.auth.signOut(); } catch (_) {}
              cleanup();
              resolve(false);
              return;
            }
            errBox.textContent = (e && e.message ? e.message : 'Code didn\u2019t verify.') + ' ' + (MAX_WRONG - wrongCount) + ' tries remaining.';
            inputLive.value = '';
            inputLive.focus();
            verifyBtnLive.disabled = false;
            verifyBtnLive.textContent = 'Verify';
          }
        }
      }

      verifyBtnLive.addEventListener('click', handleVerify);
      inputLive.addEventListener('keydown', (e) => { if (e.key === 'Enter') handleVerify(); });

      recoveryLinkLive.addEventListener('click', (e) => {
        e.preventDefault();
        if (mode === 'totp') {
          mode = 'recovery';
          subtitle.textContent = 'Enter one of your recovery codes. We accept XXXX-XXXX format.';
          inputLive.placeholder = 'XXXX-XXXX';
          inputLive.maxLength = 9;
          inputLive.style.letterSpacing = '0.15em';
          recoveryLinkLive.textContent = 'Use authenticator app code instead';
          inputLive.value = '';
          errBox.textContent = '';
          inputLive.focus();
        } else {
          mode = 'totp';
          subtitle.textContent = 'Enter the 6-digit code from your authenticator app.';
          inputLive.placeholder = '000000';
          inputLive.maxLength = 6;
          inputLive.style.letterSpacing = '0.3em';
          recoveryLinkLive.textContent = 'Lost your authenticator? Use a recovery code';
          inputLive.value = '';
          errBox.textContent = '';
          inputLive.focus();
        }
      });

      backLinkLive.addEventListener('click', async (e) => {
        e.preventDefault();
        try { await supabase.auth.signOut(); } catch (_) {}
        cleanup();
        resolve(false);
      });
    });
  }

  // ============================================================================
  // Check if the signed-in user has a pending soft-delete. If so, pin a
  // persistent banner at the top of the dashboard with "Cancel deletion".
  // ============================================================================
  async function checkDeletionStatus() {
    if (!supabase) return;
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;
    try {
      const r = await fetch(COORDINATOR + '/v1/user/deletion-status', {
        headers: { 'Authorization': 'Bearer ' + session.access_token },
      });
      if (!r.ok) return;
      const data = await r.json();
      const banner = $('deletion-banner');
      if (!banner) return;
      if (!data.pending_deletion) {
        banner.style.display = 'none';
        return;
      }
      const scheduledDate = data.scheduled_deletion
        ? new Date(data.scheduled_deletion).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
        : 'soon';
      const text = $('deletion-banner-text');
      if (text) text.innerHTML = 'Your account is scheduled for deletion on <strong>' + escapeHTML(scheduledDate) + '</strong>. Cancel to keep your account.';
      banner.style.display = 'flex';
    } catch (_) { /* best-effort check; a failure here shouldn't block login */ }
  }

  // Wire the banner's Cancel button once on first definition.
  if ($('deletion-banner-cancel') && !window._deletionBannerWired) {
    window._deletionBannerWired = true;
    $('deletion-banner-cancel').addEventListener('click', async () => {
      if (!supabase) return;
      const btn = $('deletion-banner-cancel');
      const prev = btn.textContent;
      btn.disabled = true; btn.textContent = 'Cancelling\u2026';
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) throw new Error('Not signed in.');
        const r = await fetch(COORDINATOR + '/v1/user/delete-cancel', {
          method: 'POST',
          headers: { 'Authorization': 'Bearer ' + session.access_token },
        });
        if (!r.ok) {
          const e = await r.json().catch(() => ({}));
          throw new Error(e.message || e.error || 'Could not cancel deletion.');
        }
        $('deletion-banner').style.display = 'none';
        showToast('success', 'Account deletion cancelled. Welcome back.');
      } catch (e) {
        showToast('error', 'Failed to cancel deletion: ' + (e.message || 'unknown'));
        btn.disabled = false; btn.textContent = prev;
      }
    });
  }

  // After login/signup: check if user has a linked team
  async function handleAuthSuccess(user) {
    // AAL2 enforcement — if the user has a verified TOTP factor but the current
    // session is still aal1, make them complete a TOTP challenge before we let
    // them anywhere near the dashboard. Cancel → hard sign-out so a half-done
    // login can't leak a useable session.
    try {
      const elevated = await enforceAAL2IfRequired();
      if (!elevated) return; // promptTOTPChallenge handled the sign-out + UI
    } catch (e) {
      // enforceAAL2IfRequired() now fails open/closed internally and should
      // not throw, but if it ever does, DO NOT sign the user out — a
      // transient error in the (client-side, advisory) MFA pre-check is not a
      // security event, and signing out here was a path to the "hard refresh
      // logs me out" bug. Log and proceed; the server still enforces real
      // authz, and a user with no MFA must never be ejected by a flaky
      // mfa.* call on boot.
      // eslint-disable-next-line no-console
      console.warn('AAL2 pre-check errored; proceeding without sign-out:', e);
    }

    // After AAL2, check if the account is in the grace-period pending deletion
    // and, if so, show the banner. Non-fatal — banner is informational.
    try { await checkDeletionStatus(); } catch (_) {}

    // If the user was redirected here from /device (because they needed to
    // sign in before approving), bounce them back. sessionStorage is cleared
    // on consumption to avoid loops.
    try {
      const deviceReturnTo = sessionStorage.getItem('ac-device-return-to');
      if (deviceReturnTo) {
        sessionStorage.removeItem('ac-device-return-to');
        location.replace(deviceReturnTo);
        return;
      }
    } catch (_) {}

    // Check localStorage for a saved team token
    const savedTeamToken = localStorage.getItem('ac-team-token');
    const savedHmacKey = localStorage.getItem('ac-hmac-key');
    if (savedTeamToken) {
      state.token = savedTeamToken;
      // Idempotent auto-link: bind the Supabase user to whatever tenant the
      // team token represents. Necessary because:
      //   1. agc team create writes tokens but not user_teams (it's
      //      server-side anonymous — no JWT to attribute to).
      //   2. api() prefers the Supabase JWT (api.js:77-84) over state.token.
      //   3. JWT auth requires a user_teams row (coordinator.ts:1752).
      // Net: a user who created a team via CLI, then signed into the
      // dashboard, would get blank pages forever — JWT calls 401, dashboard
      // misreads as "no data" and never asks to relink. /v1/teams/link is
      // ON CONFLICT DO UPDATE, so running it every boot is cheap + safe.
      try {
        if (supabase) {
          const { data: { session } } = await supabase.auth.getSession();
          if (session) {
            const linkRes = await fetch(COORDINATOR + '/v1/teams/link', {
              method: 'POST',
              headers: {
                'Authorization': 'Bearer ' + session.access_token,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ team_token: savedTeamToken }),
            }).catch(() => null);
            // Persist the bound tenant_id so api.js can send X-Team-ID on
            // every subsequent JWT-authed request. Without this, JWT auth
            // defaults to the oldest user_teams row (coordinator.ts:1747)
            // and the dashboard renders empty for users with multiple teams.
            if (linkRes && linkRes.ok) {
              try {
                const linkBody = await linkRes.json();
                if (linkBody && linkBody.tenant_id) {
                  localStorage.setItem('ac-tenant-id', linkBody.tenant_id);
                  // Link this team to the user's marketing contact (if they
                  // opted in at signup) so the weekly digest can resolve their
                  // team's metrics. UPDATE-only server-side — never enrolls a
                  // non-consenter. Fire-and-forget; never block the dashboard.
                  if (session.user && session.user.email) {
                    fetch('https://subscribe.agentcollision.com/v1/marketing/link-tenant', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        email: session.user.email,
                        supabaseUserId: session.user.id,
                        tenantId: linkBody.tenant_id,
                      }),
                    }).catch(() => {});
                  }
                }
              } catch (_) {/* non-fatal */}
            }
          }
        }
      } catch (_) {/* non-fatal */}
      // Validate the saved team token via /v1/teams/info. ONLY a failure of
      // THIS call means the token is bad and we re-link. Everything after a
      // successful teams/info (showDashboard, key load, boot) must NOT be able
      // to wipe the token or divert to login.
      //
      // THE BUG (confirmed via a [boot] console trace on the real session):
      // the old shape ran `await loadHmacKey(savedHmacKey)` AFTER
      // showDashboard() but inside the SAME try{}, whose catch{} did
      // `localStorage.removeItem('ac-team-token')`. loadHmacKey() does crypto +
      // a daemon port-scan and can throw; when it did, the catch wiped the
      // team token and the user fell through to the login form — i.e. "hard
      // refresh logs me out," every reload, for a perfectly valid session.
      // The HMAC key is best-effort file-path decryption; it must never gate
      // dashboard access or touch the auth token.
      let teamsInfoOk = false;
      try {
        await api('/v1/teams/info');
        teamsInfoOk = true;
      } catch {
        // Token genuinely expired or invalid — ask to re-link.
        localStorage.removeItem('ac-team-token');
      }
      if (teamsInfoOk) {
        showDashboard();
        boot();
        // Best-effort key load, fully isolated (non-awaited, swallowed) so it
        // can never wipe the token or divert to login.
        try {
          if (savedHmacKey) loadHmacKey(savedHmacKey).catch(() => {});
        } catch (_) { /* key load is non-critical */ }
        return;
      }
    }

    // Check URL params (from `agc team dashboard`)
    const params = new URLSearchParams(location.search);
    const urlToken = params.get('token');
    const urlKey = params.get('key');
    if (urlToken) {
      state.token = urlToken;
      try {
        await api('/v1/teams/info');
        localStorage.setItem('ac-team-token', urlToken);
        if (urlKey) localStorage.setItem('ac-hmac-key', urlKey);
        // Clean URL
        const clean = new URL(location.href);
        clean.searchParams.delete('token');
        clean.searchParams.delete('key');
        history.replaceState({}, '', clean.pathname + (clean.search === '?' ? '' : clean.search));
        showDashboard();
        await loadHmacKey(urlKey);
        boot();
        return;
      } catch {
        // Bad token — fall through to link form
      }
    }

    // Shareable invite link: ?join=CODE auto-fills the join step
    const joinCode = params.get('join');
    if (joinCode) {
      showLinkForm(user);
      showOnboardStep('join');
      $('onboard-join-code').value = joinCode;
      // Clean URL
      const clean = new URL(location.href);
      clean.searchParams.delete('join');
      history.replaceState({}, '', clean.pathname + (clean.search === '?' ? '' : clean.search));
      return;
    }

    // No saved team token, but the user might already belong to a team
    // (e.g. they just accepted an email invitation). Check the server.
    //
    // IMPORTANT: once we've confirmed the user is on a team, we MUST show the
    // dashboard. The previous shape wrapped the team check AND the (throwable,
    // non-critical) HMAC-key load in ONE try{}, with an empty catch that fell
    // through to showLinkForm(). So if loadHmacKey()/tryAutoImportHmacKey()
    // threw — or any post-confirmation step did — a logged-in user who IS on a
    // team got silently dumped to the login/onboarding form. That presented
    // as "hard refresh logs me out." The HMAC key is best-effort decryption
    // for file paths; it must never gate access to the dashboard. So we now
    // (a) determine team membership, (b) show the dashboard, THEN (c) attempt
    // the key load in its own guarded scope.
    let onTeam = false;
    // transientFail = the membership check couldn't get a DEFINITIVE answer
    // (network error, Cloudflare 429/error-1027, or a 5xx) — as opposed to a
    // clean "this user is on 0 teams." A transient failure must NOT dump a
    // logged-in user to the onboarding/sign-in form (it reads as "hard refresh
    // logged me out"); we keep them on the dashboard, whose own pollers back
    // off via the api.js breaker and recover when the coordinator does.
    let transientFail = false;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        let teamsR;
        try {
          teamsR = await fetch(COORDINATOR + '/v1/user/teams', {
            headers: { 'Authorization': 'Bearer ' + session.access_token },
          });
        } catch (netErr) {
          // Thrown fetch = network/CORS-level failure. On the cloud coordinator
          // the dominant cause is the Cloudflare rate-limit page (429 /
          // error-1027), which has no CORS headers and surfaces as a TypeError.
          // Treat as transient, never as "no team."
          transientFail = true;
          throw netErr;
        }
        // A 429 (rate-limit) or 5xx (coordinator hiccup) is transient too — the
        // server didn't actually tell us the user has no teams.
        if (teamsR.status === 429 || teamsR.status >= 500) {
          transientFail = true;
          throw new Error('coordinator_unavailable_' + teamsR.status);
        }
        if (teamsR.ok) {
          const teamsData = await teamsR.json();
          const myTeams = teamsData.teams || [];
          if (myTeams.length > 0) {
            onTeam = true;
            // Already on a team (either via Supabase signup + /accept, or
            // previous session). api()/apiWithMessage() re-fetch the
            // Supabase JWT per call, so no token needs stored in state.
            // Seed state.token with a sentinel so any non-api() code path
            // that reads it still has a non-null value; api() will override.
            state.token = session.access_token;
            // Load HMAC key for client-side decryption of file paths in
            // the Events / Files / Leases pages. Tries two sources, in
            // order, before giving up:
            //   1. localStorage cached key — present after a prior import
            //      or a `?key=` deep link from `agc team dashboard`.
            //   2. Local daemon's /v1/team/hmac-key — works whenever the
            //      user is on the machine that runs their daemon. This is
            //      the path that makes "I just signed in fresh on this
            //      laptop" Just Work without surfacing a manual banner.
            // Both are best-effort; the page renders either way, but
            // without a key every encrypted field falls back to its
            // hashed wire form ("file:h:...").
            // Show the dashboard + boot FIRST — confirmed-on-a-team is all
            // that's required for access. The HMAC-key load below is
            // best-effort decryption and is intentionally NOT awaited here, so
            // a key failure can never divert this user to the login form.
            showDashboard();
            boot();
            // Best-effort HMAC key load for client-side file-path decryption.
            // Fully isolated: any failure is swallowed and does not affect the
            // already-shown dashboard. (Previously this ran BEFORE
            // showDashboard inside the same try{} as the team check, so a
            // throw here silently fell through to showLinkForm — presenting as
            // "hard refresh logs me out.")
            try {
              const cachedKey = localStorage.getItem('ac-hmac-key');
              if (cachedKey) {
                loadHmacKey(cachedKey).catch(() => {});
              } else {
                tryAutoImportHmacKey().catch(() => {});
              }
            } catch (_) { /* key load is non-critical */ }
            return;
          }
        }
      }
    } catch (e) {
      // The team-membership check itself failed. If we already determined the
      // user is on a team, do NOT fall through to the login form.
      // eslint-disable-next-line no-console
      console.warn('team-membership check errored:', e);
      if (onTeam) {
        showDashboard();
        boot();
        return;
      }
      // Transient failure (network / 429 / 5xx): we could NOT confirm whether
      // the user has a team. A logged-in user must not be bounced to the
      // onboarding/sign-in screen over a temporary coordinator outage — that's
      // the "hard refresh logged me out" bug. Show the dashboard (its pollers
      // back off via the api.js breaker and recover) with a reconnecting
      // banner, rather than presenting first-run setup.
      if (transientFail || isCoordinatorCooling()) {
        showCoordinatorReconnecting();
        showDashboard();
        boot();
        return;
      }
    }

    // No team and no invitation (a DEFINITIVE answer) — show onboarding.
    showLinkForm(user);
  }

  // Non-blocking banner shown when the coordinator is temporarily unreachable
  // (Cloudflare 429 / error-1027 / network). Distinct from the auth screen —
  // the user IS logged in; the service is just rate-limited or briefly down.
  // Idempotent: re-shows the same single banner element. Auto-dismisses once a
  // coordinator call succeeds (the api.js breaker closes), polled here.
  function showCoordinatorReconnecting() {
    let bar = document.getElementById('coordinator-reconnect-bar');
    if (!bar) {
      bar = document.createElement('div');
      bar.id = 'coordinator-reconnect-bar';
      bar.style.cssText =
        'position:fixed;top:0;left:0;right:0;z-index:9999;padding:8px 16px;' +
        'background:var(--warning-bg,#3a2e00);color:var(--warning,#f0c000);' +
        'font-size:13px;text-align:center;font-family:inherit;';
      bar.textContent =
        'Reconnecting to the coordinator… it’s temporarily rate-limited or unavailable. Your session is still active.';
      document.body.appendChild(bar);
    }
    bar.style.display = '';
    // Probe the coordinator periodically; hide the bar once a call SUCCEEDS.
    // We probe with a real api() call (not just reading the breaker) because
    // the boot raw-fetch path may have set transientFail WITHOUT tripping the
    // api.js breaker — so "breaker closed" alone doesn't prove recovery. api()
    // resets the breaker on a clean response, so a success here is authoritative.
    if (window._coordinatorReconnectTimer) return; // single poller
    window._coordinatorReconnectTimer = setInterval(async () => {
      if (isCoordinatorCooling()) return; // breaker still open — don't hammer
      try {
        await api('/v1/user/teams');
        const b = document.getElementById('coordinator-reconnect-bar');
        if (b) b.style.display = 'none';
        clearInterval(window._coordinatorReconnectTimer);
        window._coordinatorReconnectTimer = null;
      } catch (_) {
        // still down — leave the bar up, try again next tick.
      }
    }, 8000);
  }

  // Link team token (used by "I have a token" path in onboarding)
  $('auth-link-btn').addEventListener('click', async () => {
    const teamToken = $('auth-team-token').value.trim();
    if (!teamToken) { showOnboardError('Team token is required.'); return; }
    $('auth-link-btn').disabled = true;
    $('auth-link-btn').textContent = 'Connecting...';
    try {
      // If logged into Supabase, link the user to the team via the coordinator
      // (this creates a user_teams row so future dashboard loads don't need
      // the team token — Supabase JWT alone will authenticate).
      if (supabase) {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          const r = await fetch(COORDINATOR + '/v1/teams/link', {
            method: 'POST',
            headers: {
              'Authorization': 'Bearer ' + session.access_token,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ team_token: teamToken }),
          });
          if (!r.ok) {
            const err = await r.json().catch(() => ({}));
            throw new Error(err.message || err.error || 'Link failed');
          }
          track('team.linked');
        }
      }

      // Always validate the team token via /v1/teams/info, save for local use
      state.token = teamToken;
      await api('/v1/teams/info');
      localStorage.setItem('ac-team-token', teamToken);
      const hmacKey = $('auth-hmac-key').value.trim();
      if (hmacKey) localStorage.setItem('ac-hmac-key', hmacKey);
      showDashboard();
      await loadHmacKey(hmacKey);
      boot();
    } catch (e) {
      state.token = null;
      showOnboardError('Invalid team token: ' + e.message);
    } finally {
      $('auth-link-btn').disabled = false;
      $('auth-link-btn').textContent = 'Connect';
    }
  });

  // ---- Onboarding wizard ----
  function showOnboardStep(step) {
    ['choose', 'create', 'join', 'token', 'created'].forEach(s => {
      const el = $('onboard-step-' + s);
      if (el) el.style.display = s === step ? '' : 'none';
    });
    $('onboard-error').style.display = 'none';
  }
  function showOnboardError(msg) {
    $('onboard-error').textContent = msg;
    $('onboard-error').style.display = '';
  }

  // Choice buttons
  document.addEventListener('click', (e) => {
    const choice = e.target.closest('.onboard-choice');
    if (choice) {
      const c = choice.dataset.choice;
      if (c === 'solo') {
        // Solo path: no team name needed. Create a Personal workspace and
        // take them straight to "install on your machine" view.
        createSoloWorkspace();
        return;
      }
      showOnboardStep(c);
      return;
    }
    const back = e.target.closest('.onboard-back');
    if (back) {
      e.preventDefault();
      showOnboardStep('choose');
      return;
    }
    const adv = e.target.closest('.onboard-advanced-link');
    if (adv) {
      e.preventDefault();
      showOnboardStep('token');
      return;
    }
  });

  // Solo path: auto-creates a single-user workspace named "Personal" so the
  // user doesn't have to invent a team name they don't need. Mirrors the
  // create-team flow but skips the name input.
  async function createSoloWorkspace() {
    const choose = $('onboard-step-choose');
    if (choose) {
      choose.innerHTML = '<div style="padding: 40px 0; text-align: center; color: var(--text-secondary); font-size: 14px;">'
        + '<div style="width: 24px; height: 24px; border: 2px solid var(--border); border-top-color: var(--accent); border-radius: 50%; margin: 0 auto 16px; animation: accept-spin 0.8s linear infinite;"></div>'
        + 'Setting up your workspace&hellip;</div>'
        + '<style>@keyframes accept-spin{to{transform:rotate(360deg)}}</style>';
    }
    try {
      const r = await fetch(COORDINATOR + '/v1/teams/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: 'Personal' }),
      });
      const data = await r.json();
      if (!r.ok) { showOnboardError(data.message || data.error || 'Setup failed'); showOnboardStep('choose'); return; }

      state.token = data.admin_token;
      localStorage.setItem('ac-team-token', data.admin_token);
      localStorage.setItem('ac-hmac-key', data.hmac_key_b64);
      await loadHmacKey(data.hmac_key_b64);

      if (supabase) {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          // Keep the link response. Two reasons this is not fire-and-forget:
          //
          //  1. The user_teams row it creates is what the coordinator's JWT
          //     auth resolves a tenant from, so every JWT-authed call before
          //     it lands comes back 401.
          //  2. tenant_id has to be persisted for api() to send X-Team-ID on
          //     subsequent calls. Without it a solo user falls through to the
          //     coordinator's "oldest user_teams row" default — correct while
          //     they have one team, wrong the moment they have two.
          //
          // Mirrors the returning-user relink path above. A failed link is
          // still non-fatal: onboarding continues and the relink on next boot
          // repairs it (/v1/teams/link is ON CONFLICT DO UPDATE).
          const linkRes = await fetch(COORDINATOR + '/v1/teams/link', {
            method: 'POST',
            headers: { 'Authorization': 'Bearer ' + session.access_token, 'Content-Type': 'application/json' },
            body: JSON.stringify({ team_token: data.admin_token }),
          }).catch(() => null);
          if (linkRes && linkRes.ok) {
            try {
              const linkBody = await linkRes.json();
              if (linkBody && linkBody.tenant_id) {
                localStorage.setItem('ac-tenant-id', linkBody.tenant_id);
              }
            } catch {}
          }
        }
      }

      track('onboard.solo_created');
      // Drop straight into the dashboard — the persistent setup banner + My
      // Machines panel will show the install command without another step.
      showDashboard();
      boot();
    } catch (err) {
      showOnboardError((err && err.message) || 'Setup failed');
      showOnboardStep('choose');
    }
  }

  // Create team via coordinator
  $('onboard-create-btn').addEventListener('click', async () => {
    const name = ($('onboard-create-name').value || '').trim() || 'My Team';
    const btn = $('onboard-create-btn');
    btn.disabled = true;
    btn.textContent = 'Creating...';
    $('onboard-error').style.display = 'none';
    try {
      const r = await fetch(COORDINATOR + '/v1/teams/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      });
      const data = await r.json();
      if (!r.ok) { showOnboardError(data.message || data.error || 'Create failed'); return; }

      // Save token + HMAC key
      state.token = data.admin_token;
      localStorage.setItem('ac-team-token', data.admin_token);
      localStorage.setItem('ac-hmac-key', data.hmac_key_b64);
      await loadHmacKey(data.hmac_key_b64);

      // Link Supabase user to team (so future dashboards auth via JWT alone)
      if (supabase) {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          await fetch(COORDINATOR + '/v1/teams/link', {
            method: 'POST',
            headers: {
              'Authorization': 'Bearer ' + session.access_token,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ team_token: data.admin_token }),
          }).catch(() => {}); // Non-fatal — team token in localStorage still works
        }
      }

      // Show success with invite code
      $('onboard-created-msg').textContent = 'You are now the admin of "' + data.team_name + '".';
      $('onboard-created-code').textContent = data.invite_code;
      window._onboardInviteCode = data.invite_code;
      showOnboardStep('created');
      track('team.created', { team_id: data.team_id });
    } catch (e) {
      showOnboardError('Network error: ' + e.message);
    } finally {
      btn.disabled = false;
      btn.textContent = 'Create Team';
    }
  });

  // Join team via coordinator (redeem invite)
  $('onboard-join-btn').addEventListener('click', async () => {
    const code = ($('onboard-join-code').value || '').trim();
    if (!code) { showOnboardError('Invite code is required.'); return; }
    const btn = $('onboard-join-btn');
    btn.disabled = true;
    btn.textContent = 'Joining...';
    $('onboard-error').style.display = 'none';
    try {
      const r = await fetch(COORDINATOR + '/v1/teams/redeem-invite', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ invite_token: code }),
      });
      if (!r.ok) {
        const msg = (await r.text()).trim();
        if (msg.includes('not found')) showOnboardError('Invite code not found. Double-check the code.');
        else if (msg.includes('expired')) showOnboardError('Invite code expired. Ask your teammate for a new one.');
        else if (msg.includes('revoked')) showOnboardError('Invite code was revoked. Ask your teammate for a new one.');
        else showOnboardError('Join failed: ' + msg);
        return;
      }
      const data = await r.json();
      state.token = data.laptop_token;
      localStorage.setItem('ac-team-token', data.laptop_token);
      localStorage.setItem('ac-hmac-key', data.hmac_key_b64);
      await loadHmacKey(data.hmac_key_b64);

      // Link Supabase user to team for future JWT-based auth
      if (supabase) {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          await fetch(COORDINATOR + '/v1/teams/link', {
            method: 'POST',
            headers: {
              'Authorization': 'Bearer ' + session.access_token,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ team_token: data.laptop_token }),
          }).catch(() => {});
        }
      }

      track('team.joined', { team_id: data.team_id });
      showDashboard();
      boot();
    } catch (e) {
      showOnboardError('Network error: ' + e.message);
    } finally {
      btn.disabled = false;
      btn.textContent = 'Join Team';
    }
  });

  // Copy shareable invite link
  $('onboard-copy-link').addEventListener('click', () => {
    const code = window._onboardInviteCode;
    if (!code) return;
    const link = 'https://dashboard.agentcollision.com/?join=' + encodeURIComponent(code);
    navigator.clipboard.writeText(link).then(() => {
      $('onboard-copy-link').textContent = 'Copied to clipboard';
      setTimeout(() => { $('onboard-copy-link').textContent = 'Copy shareable link'; }, 2000);
    });
  });

  // Continue to dashboard after team creation
  $('onboard-continue').addEventListener('click', () => {
    showDashboard();
    boot();
  });

  // ----- Init -----
  // Check for existing Supabase session, URL params, or show auth.
  // Wrapped in try/catch so a failure always falls back to showing the auth screen.
  (async () => {
    try {
      // Phase 7+ unification fix: in local-daemon mode there's no
      // Supabase, no auth screen, no team token to look up. The
      // daemon serves the dashboard over loopback and trusts the
      // caller. Skip the entire cloud-side init flow and go
      // straight to boot. Without this short-circuit, the
      // unconditional `supabase.auth.getSession()` call below
      // throws "Cannot read properties of null" because PR #7
      // sets `supabase = null` in daemon mode.
      const isDaemonMode = window.AC_CONFIG && window.AC_CONFIG.DEPLOY_MODE === 'local-daemon';
      if (isDaemonMode) {
        // Load the HMAC key BEFORE boot() so the very first
        // fetchAll() pass can decrypt event payload_hints. Without
        // this, the first 200 events come back hashed and only the
        // next poll-tick fills in real paths (15-60s user-visible
        // delay).
        //
        // Strategy: always prefer the daemon's current key over the
        // localStorage cache. The daemon is the single source of
        // truth, fetch is cheap (~5-35ms), and this side-steps the
        // stale-cache class of bugs (key rotation, tenant switch).
        // Falls back to localStorage cache only if the daemon fetch
        // times out.
        //
        // tryAutoImportHmacKey uses daemon-discovery.js's fetcher
        // which port-scans 127.0.0.1:7777-7790. Treated as
        // cross-origin from localhost:NNNN by the browser, so
        // Origin is sent and the daemon's loopback-bridge gate
        // accepts. (Same-origin fetches strip Origin on simple GETs
        // and got 403.)
        try {
          await Promise.race([
            tryAutoImportHmacKey(),
            new Promise((resolve) => setTimeout(resolve, 1500)),
          ]);
        } catch (_) {}
        // Fallback to localStorage cache only if the daemon
        // fetch didn't land in time. Better stale-key than no-key.
        if (!state.hmacKey) {
          try {
            const cached = localStorage.getItem('ac-hmac-key');
            if (cached) await loadHmacKey(cached);
          } catch (_) {}
        }
        showDashboard();
        // We already attempted one daemon import above — tell boot's HMAC
        // auto-loader to skip the immediate re-attempt and go straight to the
        // backoff retry loop (no redundant duplicate fetch on cold boot).
        boot(true);
        return;
      }

      // Accept-invitation route: /accept?token=... — short-circuit everything else
      if (location.pathname === '/accept') {
        const acceptTok = new URLSearchParams(location.search).get('token');
        if (acceptTok) {
          await renderAcceptInvitationPage(acceptTok);
          return;
        }
      }

      // Device-auth approval route: /device[?code=XXXX-XXXX] — the `agc login`
      // command on a new machine opens this URL in the user's browser so they
      // can confirm they want to link the daemon to their account.
      if (location.pathname === '/device') {
        await renderDeviceApprovalPage();
        return;
      }

      const params = new URLSearchParams(location.search);
      const urlToken = params.get('token');
      const urlKey = params.get('key');

      // If URL has token (from `agc team dashboard`), try direct auth (skip Supabase)
      if (urlToken) {
        state.token = urlToken;
        try {
          await api('/v1/teams/info');
          localStorage.setItem('ac-team-token', urlToken);
          if (urlKey) localStorage.setItem('ac-hmac-key', urlKey);
          const clean = new URL(location.href);
          clean.searchParams.delete('token');
          clean.searchParams.delete('key');
          history.replaceState({}, '', clean.pathname + (clean.search === '?' ? '' : clean.search));
          showDashboard();
          await loadHmacKey(urlKey);
          boot();
          return;
        } catch { /* fall through */ }
      }

      // Password-recovery short-circuit. When the user clicks the reset-password
      // email, Supabase creates a session from the recovery token in the URL hash
      // (`#access_token=...&type=recovery`). If we let the normal session check
      // below run, handleAuthSuccess() would boot them into the dashboard
      // instead of letting them set a new password. __landedOnRecoveryLink was
      // captured at script-eval time, before createClient() stripped the hash.
      if (__landedOnRecoveryLink) {
        showResetForm();
        return;
      }

      // Check Supabase session
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        await handleAuthSuccess(session.user);
        return;
      }

      // Check saved team token (non-Supabase direct login)
      const savedTeamToken = localStorage.getItem('ac-team-token');
      if (savedTeamToken) {
        state.token = savedTeamToken;
        // Same idempotent auto-link as handleAuthSuccess — if the user is
        // also signed into Supabase, make sure the user_teams binding
        // exists so api()'s JWT-preferring path doesn't 401 on every call.
        try {
          if (supabase) {
            const { data: { session } } = await supabase.auth.getSession();
            if (session) {
              await fetch(COORDINATOR + '/v1/teams/link', {
                method: 'POST',
                headers: {
                  'Authorization': 'Bearer ' + session.access_token,
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({ team_token: savedTeamToken }),
              }).catch(() => {/* non-fatal */});
            }
          }
        } catch (_) {/* non-fatal */}
        try {
          await api('/v1/teams/info');
          showDashboard();
          await loadHmacKey(localStorage.getItem('ac-hmac-key'));
          boot();
          return;
        } catch {
          localStorage.removeItem('ac-team-token');
        }
      }
    } catch (e) {
      console.error('Init failed:', e);
    }
    showAuth();
  })();

  // Listen for auth state changes (e.g. email confirmation redirect)
  // Skip INITIAL_SESSION — handled by init IIFE above.
  // Daemon mode: supabase is null, nothing to listen to.
  let initDone = false;
  if (supabase) supabase.auth.onAuthStateChange(async (event, session) => {
    if (event === 'INITIAL_SESSION') {
      initDone = true;
      // If the user landed here via a password-recovery link, the INITIAL_SESSION
      // carries the recovery session. Use the flag captured at script-eval time
      // rather than re-reading location.hash, which is empty by now (Supabase
      // stripped it synchronously during createClient).
      if (session && __landedOnRecoveryLink) {
        showResetForm();
      }
      return;
    }
    if (!initDone) return; // ignore events fired before init completes
    if (event === 'PASSWORD_RECOVERY') {
      showResetForm();
      return;
    }
    // DEVICE-APPROVE return-to (must run even if `booted`). When a user with a
    // saved TEAM TOKEN opens /device and clicks "Sign in", they land on `/`,
    // the team token boots the dashboard, and `booted` becomes true BEFORE
    // they finish the Supabase login. So when SIGNED_IN finally fires, the
    // normal `!booted` handler below SKIPS handleAuthSuccess — and the pending
    // `ac-device-return-to` bounce never happens. The user is stuck on the
    // dashboard, /device is never revisited, and they can't approve. Honor the
    // device return-to here regardless of `booted` (the session is now real,
    // so /device will read it and show Approve). Confirmed by a Playwright
    // repro of the exact dual-auth (team-token + Supabase) state.
    if (event === 'SIGNED_IN' && session && !recoveryInProgress) {
      let pendingDevice = null;
      try { pendingDevice = sessionStorage.getItem('ac-device-return-to'); } catch (_) {}
      if (pendingDevice) {
        try { sessionStorage.removeItem('ac-device-return-to'); } catch (_) {}
        location.replace(pendingDevice);
        return;
      }
    }
    if (event === 'SIGNED_IN' && session && !booted) {
      // Don't auto-boot into the dashboard if the user is in the middle of
      // setting a new password. updateUser() fires SIGNED_IN as a side effect
      // and we handle post-reset boot explicitly in the reset button handler.
      if (recoveryInProgress) return;
      await handleAuthSuccess(session.user);
    }
    if (event === 'SIGNED_OUT') {
      // SPURIOUS SIGNED_OUT GUARD. Supabase fires SIGNED_OUT not only on a real
      // sign-out but also transiently — notably right after a hard reload when
      // a background token refresh momentarily fails/races, even though the
      // session is still valid and recoverable. The old handler unconditionally
      // wiped state.token + ac-team-token and called showAuth() on EVERY
      // SIGNED_OUT, which logged a perfectly-valid user out on reload. Before
      // tearing down, re-check whether a session actually still exists; if it
      // does, this was a spurious event — ignore it and keep the user on the
      // dashboard.
      let stillSignedIn = false;
      try {
        if (supabase) {
          const { data } = await supabase.auth.getSession();
          stillSignedIn = !!(data && data.session);
        }
      } catch { /* treat as signed out */ }
      if (stillSignedIn) {
        // Spurious — the session is fine. Do nothing.
        return;
      }
      booted = false;
      state.token = null;
      localStorage.removeItem('ac-team-token');
      // If the user is mid-recovery (e.g. a background token refresh failed),
      // leave them on the reset form. The updateUser() call will surface a
      // real error if the recovery session is actually dead.
      if (recoveryInProgress) return;
      // If the user is already on the auth screen (e.g. typing into the
      // forgot-password form), don't yank them back to login.
      const onAuthScreen = $('auth-screen').style.display !== 'none';
      if (onAuthScreen) return;
      showAuth();
    }
  });


  // showToast displays a brief banner at the bottom-right corner of
  // the dashboard. Auto-dismisses after 4 seconds. Replaces any
  // currently-shown toast (we never queue more than one).


  // =============================================================
  // PHASE 4: WHOAMI BADGE
  //
  // Fetches GET /v1/me on boot and populates the sidebar badge with
  // the user's email, role, and a Gravatar avatar. Refreshed
  // whenever the user changes accounts (`agc account use <name>`)
  // and clicks "Refresh" or refreshes the dashboard tab.
  //
  // We don't poll continuously — identity rarely changes per
  // session, and the existing 5s lease/event poll cycle would
  // hammer the daemon's filesystem with redundant ResolveActive
  // calls (each one re-reads identity.yaml). Polling once per
  // 5 minutes is plenty.
  //
  // Hidden in cloud-pages mode by default — cloud's auth shell +
  // account page already surface identity, the badge would be
  // redundant. When cloud-pages mode wires up its own /v1/me
  // equivalent (Phase 5+), we can flip the gate.
  // =============================================================
  // whoamiCache moved to src/whoami.js (Phase K Tier 2 prep).
  // Read via getWhoami(), replace via setWhoami(x), shallow-merge
  // via patchWhoami({...}). The bridge entry below still works
  // because getWhoami() returns the live reference.


  // Role color map. Drives the chip's accent color so a human
  // session is visually distinct from a sandbox one. Keyed by BOTH the
  // v0.39.0 vocabulary (human/reviewer/background/batch/sandbox) and the
  // legacy v1 names (Driver/Responder/Routine/Worker/Sandbox) so a
  // session reporting either still gets a color.
  const ROLE_COLORS = {
    human:     { bg: 'rgba(96, 165, 250, 0.15)',  fg: 'var(--accent)' },
    reviewer:  { bg: 'rgba(251, 191, 36, 0.15)',  fg: '#f59e0b' },
    background: { bg: 'rgba(168, 85, 247, 0.15)', fg: '#a855f7' },
    batch:     { bg: 'rgba(148, 163, 184, 0.15)', fg: 'var(--text-secondary)' },
    sandbox:   { bg: 'rgba(239, 68, 68, 0.15)',   fg: '#ef4444' },
    // Legacy v1 aliases (same colors).
    Driver:    { bg: 'rgba(96, 165, 250, 0.15)',  fg: 'var(--accent)' },
    Responder: { bg: 'rgba(251, 191, 36, 0.15)',  fg: '#f59e0b' },
    Routine:   { bg: 'rgba(168, 85, 247, 0.15)',  fg: '#a855f7' },
    Worker:    { bg: 'rgba(148, 163, 184, 0.15)', fg: 'var(--text-secondary)' },
    Sandbox:   { bg: 'rgba(239, 68, 68, 0.15)',   fg: '#ef4444' },
    // Solo: shown when the daemon hasn't been signed in to a team
    // OIDC. Visually muted because there's no team/role context to
    // signal — it's not a real role, just "you, alone".
    Solo:      { bg: 'rgba(148, 163, 184, 0.12)', fg: 'var(--text-secondary)' },
  };

  async function loadAndRenderWhoami() {
    const cfg = window.AC_CONFIG;
    const isCloud = cfg && cfg.DEPLOY_MODE !== 'local-daemon';

    if (isCloud) {
      // Cloud mode: pull identity from the Supabase session and merge
      // any custom profile (avatar, display_name) from the coordinator.
      // Without this branch the sidebar badge stayed at its static
      // "—" / "Solo" / empty-img placeholders forever — a black circle
      // and no name.
      if (!supabase) return;
      let session = null;
      try { ({ data: { session } } = await supabase.auth.getSession()); } catch (_) {}
      if (!session) return;
      const u = session.user || {};
      // Derive a meaningful source label from the user's linked
      // identities. The literal "supabase" label leaked the auth
      // vendor into user-facing UI; what users actually want to see
      // is the *provider they signed in with* — github, google, etc.
      // For plain email/password the source label is suppressed
      // entirely (the chip alone is enough; "· email" is noise).
      const identities = u.identities || [];
      const oauthProviders = identities.map(i => i.provider).filter(p => p && p !== 'email');
      const sourceLabel = oauthProviders[0] || '';
      setWhoami({
        email: u.email || '',
        // identity carries the Supabase user id (= the OIDC sub), matching
        // the local-daemon /v1/me contract ("OIDC subject if configured").
        // It previously carried the EMAIL, which broke every actor_subject
        // consumer: coordination_events / remote_leases store the sub UUID,
        // so the Overview "My chats" scope, the Leases "mine only" filter,
        // and the Chats mine-filter (people[].user_id === identity) all
        // compared email-vs-UUID and matched zero rows. Display surfaces
        // are unaffected — they all read `email || identity`.
        identity: u.id || u.email || '',
        source: sourceLabel,
        role: 'Solo',  // overwritten below from /v1/user/profile if a team membership exists
        avatar_data_url: null,
      });
      try {
        const r = await fetch(COORDINATOR + '/v1/user/profile', {
          headers: { 'Authorization': 'Bearer ' + session.access_token, 'Accept': 'application/json' },
        });
        if (r.ok) {
          const profile = await r.json();
          if (profile.avatar_data_url) patchWhoami({ avatar_data_url: profile.avatar_data_url });
          if (profile.display_name) patchWhoami({ display_name: profile.display_name });
        }
      } catch (_) {}
    } else {
      try {
        const r = await apiFetch('/v1/me', { headers: { 'Accept': 'application/json' } });
        if (!r.ok) return;
        setWhoami(await r.json());
      } catch (e) {
        console.warn('[whoami] /v1/me failed:', e);
        return;
      }
    }

    const badge = document.getElementById('whoami-badge');
    if (!badge) return;

    const emailEl = document.getElementById('whoami-email');
    const chipEl = document.getElementById('whoami-role-chip');
    const sourceEl = document.getElementById('whoami-source');
    const avatarEl = document.getElementById('whoami-avatar');

    // Snapshot the current whoami once — render is sync below.
    const w = getWhoami() || {};

    // Identity display: prefer email, fall back to subject, fall
    // back to "anonymous". Subject is often opaque (Google's
    // numeric id) so email is the friendlier display.
    const display = w.email || w.identity || 'anonymous';
    if (emailEl) emailEl.textContent = display;

    // Anonymous (solo, no OIDC sign-in) gets a "Solo" chip instead
    // of the enterprise role default ("Driver"). Showing "Driver" to
    // a single-machine user labels them with a team-tier role they
    // don't have and didn't pick — confusing on first encounter.
    const isAnonymous = !w.source || w.source === 'anonymous';
    if (chipEl) {
      const role = isAnonymous ? 'Solo' : (w.role || 'human');
      chipEl.textContent = role;
      const c = ROLE_COLORS[role] || ROLE_COLORS.human;
      chipEl.style.background = c.bg;
      chipEl.style.color = c.fg;
    }
    if (sourceEl) {
      // Phase 4 nicety: tell the user where the identity came from.
      // "anonymous" → no source label (the chip itself signals it).
      if (w.source && w.source !== 'anonymous') {
        sourceEl.textContent = '· ' + w.source;
      } else {
        sourceEl.textContent = '';
      }
    }
    if (avatarEl) {
      if (w.avatar_data_url) {
        // User uploaded a custom avatar — always wins.
        avatarEl.src = w.avatar_data_url;
      } else if (w.email) {
        // Email = Gravatar fallback (identicon style if no registered
        // Gravatar). Note: Gravatar can leak the user's email hash to
        // a third-party CDN — when we add a "disable Gravatar" toggle
        // it lives in Account → Privacy.
        avatarEl.src = await gravatarURL(w.email);
      } else {
        // No email = no Gravatar. Render an inline initial-letter
        // avatar via a data: SVG so the slot reads as a real
        // placeholder rather than an empty dark blob.
        avatarEl.src = inlineInitialAvatar(display);
      }
    }

    // Clicking the badge takes you to the Account page — single
    // place to view your identity, switch role / re-auth, and (in
    // cloud mode) manage email/billing. The Account nav item is
    // un-gated as of Phase 7d so this works in daemon mode too.
    badge.style.cursor = 'pointer';
    badge.setAttribute('data-tooltip', 'View your account');
    badge.setAttribute('role', 'button');
    badge.setAttribute('tabindex', '0');
    if (!badge.dataset.profileWired) {
      const goToAccount = () => navigate('account', true);
      badge.addEventListener('click', goToAccount);
      badge.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); goToAccount(); }
      });
      badge.dataset.profileWired = '1';
    }

    badge.style.display = 'flex';
  }

  // renderAccountIdentityPanel paints the daemon-mode "Identity"
  // panel on the Account page. Reuses the cached whoami data from
  // loadAndRenderWhoami() so we don't double-fetch /v1/me. The
  // panel is .daemon-only — hidden in cloud mode via the existing
  // deploy-mode CSS (cloud has its own Profile/Email panels).
  async function renderAccountIdentityPanel() {
    // Lazy-load whoami if it hasn't run yet (e.g. user navigates to
    // Account before the badge has rendered). Mirrors the badge's
    // shape so the two views stay consistent.
    let w = getWhoami();
    if (!w || !w.role) {
      try {
        const r = await apiFetch('/v1/me', { headers: { 'Accept': 'application/json' } });
        if (r.ok) {
          setWhoami(await r.json());
          w = getWhoami();
        }
      } catch {}
    }
    if (!w) w = {};
    const isAnonymous = !w.source || w.source === 'anonymous';
    const display = w.email || w.identity || 'anonymous';

    const avatarEl = document.getElementById('account-identity-avatar');
    const displayEl = document.getElementById('account-identity-display');
    const chipEl = document.getElementById('account-identity-chip');
    const sourceEl = document.getElementById('account-identity-source');
    const detailEl = document.getElementById('account-identity-detail');
    const actionsEl = document.getElementById('account-identity-actions');

    if (avatarEl) {
      if (w.email) {
        avatarEl.src = await gravatarURL(w.email);
      } else {
        avatarEl.src = inlineInitialAvatar(display);
      }
    }
    if (displayEl) displayEl.textContent = display;
    if (chipEl) {
      const role = isAnonymous ? 'Solo' : (w.role || 'human');
      chipEl.textContent = role;
      const c = ROLE_COLORS[role] || ROLE_COLORS.human;
      chipEl.style.background = c.bg;
      chipEl.style.color = c.fg;
    }
    if (sourceEl) {
      sourceEl.textContent = isAnonymous ? '· not signed in' : ('· ' + w.source);
    }
    if (detailEl) {
      if (isAnonymous) {
        detailEl.innerHTML = `
          You're running the AgentCollision daemon locally with no team identity attached.
          Your role chip says <strong>Solo</strong> because there's no team policy to apply yet.
          Sign in via OIDC to attach an identity, take on a real role, and have your decisions
          attributed to you across machines.
          <div style="margin-top: 8px; font-size: 11px; color: var(--text-tertiary);">
            Need to share your avatar across machines? Sign up at
            <a href="https://gravatar.com" target="_blank" rel="noopener" style="color: var(--accent);">gravatar.com</a>
            with the email you use here — Gravatar pairs to your email and the badge picks it up automatically.
          </div>`;
      } else {
        detailEl.innerHTML = `
          Identity verified via <code>${escapeHTML(w.source)}</code>.
          Your avatar is pulled from Gravatar — change it at
          <a href="https://gravatar.com" target="_blank" rel="noopener" style="color: var(--accent);">gravatar.com</a>
          (it pairs to your email, no AC-side setting).
          <div style="margin-top: 8px; font-size: 11px; color: var(--text-tertiary);">
            Role: <strong>${escapeHTML(w.role || 'human')}</strong>
            ${typeof w.rank === 'number' ? `· Rank ${w.rank}` : ''}
          </div>`;
      }
    }
    if (actionsEl) {
      // v0.31.9: in local-daemon mode the Sign in button is misleading
      // — it has nowhere to go (browser-side OIDC isn't wired up, and
      // showToast just tells the user to drop to terminal). Replace
      // with a code-styled inline hint pointing at `agc login` directly.
      // The HEADS UP card already tells them the same thing.
      const localDaemonMode =
        window.AC_CONFIG && window.AC_CONFIG.DEPLOY_MODE === "local-daemon";
      const signedInActions = `<button class="btn btn-sm" id="account-identity-signout-btn">Sign out</button>`;
      let anonActions;
      if (localDaemonMode) {
        anonActions = `<code style="font-size: 12px; padding: 4px 8px; background: var(--bg-secondary); border-radius: 4px; color: var(--text-secondary);">agc login</code>
           <a href="https://docs.agentcollision.com/identity.html" target="_blank" rel="noopener" style="font-size: 11px; color: var(--text-tertiary); text-decoration: none;">Learn more</a>`;
      } else {
        anonActions = `<button class="btn btn-sm" id="account-identity-signin-btn" style="background: var(--accent); color: #fff; border: none;">Sign in</button>
           <a href="https://docs.agentcollision.com/identity.html" target="_blank" rel="noopener" style="font-size: 11px; color: var(--text-tertiary); text-decoration: none;">Learn more</a>`;
      }
      actionsEl.innerHTML = isAnonymous ? anonActions : signedInActions;
      const signin = document.getElementById('account-identity-signin-btn');
      if (signin) signin.addEventListener('click', () => {
        // No in-product sign-in for daemon-mode yet — point users at
        // the CLI flow which is the authoritative path. A first-class
        // OIDC popup is on the Phase 7d backlog.
        // (v0.31.9: this handler only runs in cloud mode now; local
        // daemon shows a `agc login` code hint instead of a button.)
        showToast('info', 'Run `agc login` in your terminal to sign in. Browser-side OIDC is on the roadmap.');
      });
      const signout = document.getElementById('account-identity-signout-btn');
      if (signout) signout.addEventListener('click', () => {
        showToast('info', 'Run `agc logout` in your terminal to sign out of this machine.');
      });
    }
  }


  // Refresh every 5 minutes. Identity changes are rare enough that
  // anything more aggressive is wasted work.
  if (typeof setInterval === 'function') {
    setInterval(() => { loadAndRenderWhoami(); }, 5 * 60 * 1000);
  }

  // Initial load: fire on next tick so boot()'s fetchAll has a chance
  // to set up state first. Best-effort; failure is logged and the
  // badge stays hidden.
  setTimeout(loadAndRenderWhoami, 0);

  // Bridge for the few page modules that still consume helpers
  // owned by this bootstrap layer (auth, settings, account).
  // Consumers read `window.AC_LEGACY.<entry>` directly — the
  // src/legacy.js Proxy wrapper was deleted on 2026-05-13 (Phase
  // J final-c).
  //
  // The bootstrap.js side-effect import at the top of src/main.js
  // guarantees this assignment runs before any page module's
  // first render (page modules subscribe to a MutationObserver on
  // their section's .active class, and the section only becomes
  // active after bootstrap's IIFE has finished routing the URL).
  //
  // Entries are SHARED REFERENCES, not copies — when page modules
  // read `window.AC_LEGACY.state.events`, they get the same array the polling
  // tick mutates. Page modules don't need to subscribe to anything.
  // Bridge entries below are ONLY for symbols still owned by this
  // module that external modules consume. As the auth state machine
  // hoists out of here (deferred — see Phase J7 plan in CHANGELOG)
  // these entries shrink further. Pre-J-final cleanup pruned ~52
  // entries that were dead bridge weight from earlier migration
  // checkpoints.
  window.AC_LEGACY = {
    // Coordination page — pages/coordination.js consumes both for
    // the decision filter chips + summary line.
    // Handoffs page — src/handoffs.js's submitHandoffCompose calls
    // renderHandoffsPage on send-complete, and reads the operator's
    // identity. whoamiCache lives in src/whoami.js now; the bridge
    // re-exposes the getter for handoffs.js, which reads at call time.
    renderHandoffsPage,
    getWhoami,
    // Team page — src/team-page.js's renderTeamPageInner dispatches
    // to renderTeamV2 via legacyHelper("renderTeamV2") at the end of
    // its render. Bridged because team-page.js is loaded by main.js
    // before bootstrap finishes populating its closures.
    renderTeamV2,
    // Analytics page — src/analytics.js's upgrade-button click
    // handler reads window.AC_LEGACY.startCheckout at click time.
    startCheckout,
    // Account page — pages/account.js destructures these. supabase
    // is a `let` reassigned during init, so getter not snapshot.
    // renderAccountIdentityPanel still reads supabase + state.token
    // from this closure. The 4 refresh* helpers became direct
    // imports from account-avatar.js / account-auth.js in Tier 2n.
    getSupabase: () => supabase,
    // api.js calls showAuth() on 401 to flip to the auth screen.
    showAuth,
    renderAccountIdentityPanel,
    // account-avatar.js's clear-avatar handler calls
    // loadAndRenderWhoami after delete to repaint the sidebar.
    // Bridge entry because loadAndRenderWhoami still lives in this
    // file (importing it would create a circular dep).
    loadAndRenderWhoami,
    // account-actions.js + account-email-password.js call this
    // after a password change to flip booted/state.token and bounce
    // to the auth screen. Stays on the bridge because it mutates
    // closure-bound auth state. requireReauth + clearReauth went
    // direct in Tier 2n.
    signOutAfterPasswordChange,
  };

  // Signal that AC_LEGACY is populated. Page modules wait for this
  // before initial render if they need bridge access. Modules that
  // don't touch the bridge (e.g. home.js) can ignore it.
  document.dispatchEvent(new CustomEvent("dashboard:ready"));
}

// Invoke immediately at module import time. main.js imports this
// module FIRST, before any page module, so AC_LEGACY is populated
// by the time the first page module's top-level bindLifecycle()
// runs. This is the bundle equivalent of the old <script
// src="dashboard.js" defer></script> tag.
setupLegacyDashboard();

